import { tools } from "./metadata";
import { proxyURL, sendRequest } from "../../services/office-api";
import { render } from "solid-js/web";
import FacebookCommunityModal from "./elements/FacebookCommunityModal";
import SendMessageToSponsorModal from "./elements/SendMessageToSponsorModal";
import LegacyToolsModal from "./elements/LegacyToolsModal";
import HeaderLeft from "./elements/HeaderLeft";
import HeaderRight from "./elements/HeaderRight";
import { getTranslations } from "../../services/translation";
import GetStartedModal from "./elements/GetStartedModal";
import { setTranslations, translations } from "../../services/context";
//import { getUserLang } from "../../services/get-user-language";

import socialProofIcon from './assets/social-proof.svg';
import browserNotificationIcon from './assets/browser_notification.svg';
import livechatIcon from './assets/livechat.svg';
import exitPopupIcon from './assets/popup.svg';
import autoresponderIcon from './assets/autoresponder.svg';

type Resources = {
  accessRules: Set<string>,
  userQuotas: Set<string>,
  history: Array<string>,
  whiteLabel: { title: string, logo: string } | false,
  user: { id: number, name: string, email: string, lang: string }
  translations: { [_: string]: string },
  hasNotifications: Boolean,
  whiteLabelData: Array<string>,
}
type ToggleModal = {
  setToggleModal: Boolean,
}
customElements.define("builderall-appshell", class extends HTMLElement {
  private preparedStatement? = null;

  constructor() {
    super();

    this.attachShadow({ mode: "open" });

    (new MutationObserver(() => {
      this.querySelectorAll("builderall-banner, builderall-help, builderall-footer, builderall-enom, builderall-digital-file-repository, builderall-global-script")
        .forEach(child => {
          child.setAttribute("tool", this.getAttribute("tool"));
          child.setAttribute("token", this.getAttribute("token"));
        });
    })).observe(this.shadowRoot, { childList: true, subtree: true });
  }

  static get observedAttributes() {
    return ["token", "tool", "disable"];
  }

  attributeChangedCallback() {
    if (this.preparedStatement) clearTimeout(this.preparedStatement);

    this.preparedStatement = setTimeout(async () => {
      this.updateChildComponents();

      const cachedResources = this.fetchResourcesFromCache();
      this.mountAll(cachedResources);
      setTranslations(cachedResources.translations);

      if (!this.getAttribute("token")) return;

      const fetchedResources = await this.fetchResources();
      this.mountAll(fetchedResources);
      setTranslations(fetchedResources.translations);

      this.saveResourcesToCache(fetchedResources);
    });
  }


  async fetchResources(): Promise<Resources> {
    const [accessRules, history, whiteLabel, user, hasNotifications, userQuotas, whiteLabelData] = await Promise.all([
      this.getAccessRules(),
      this.syncHistory(),
      this.getWhiteLabel(),
      this.getUser(),
      this.hasNotifications(),
      this.getUserQuotas(),
      this.getWhiteLabelData()
    ]);

    const translations = await getTranslations(user.lang);

    return { accessRules, history, whiteLabel, user, translations, hasNotifications, userQuotas, whiteLabelData };
  }

  fetchResourcesFromCache(): Resources {
    const defaultResources: Resources = {
      accessRules: new Set<string>(),
      userQuotas: new Set<string>(),
      history: new Array<string>(),
      whiteLabel: false,
      user: {
        id: 0,
        name: "",
        email: "",
        lang: "",
      },
      translations: {},
      hasNotifications: false,
      whiteLabelData: new Set<string>()
    };

    const cached = localStorage.getItem("builderall-appshell--resources");
    if (!cached) return defaultResources;

    try {
      const { history, accessRules, whiteLabel, user, translations, hasNotifications, userQuotas, whiteLabelData } = JSON.parse(cached);
      return { history, accessRules: new Set(accessRules), whiteLabel, user, translations, hasNotifications, userQuotas, whiteLabelData };
    } catch (e) {
      console.warn("[appshell] unable to parse cached resources", e);
      return defaultResources;
    }
  }

  saveResourcesToCache({ history, accessRules, whiteLabel, user, translations, hasNotifications, userQuotas, whiteLabelData }: Resources) {
    localStorage.setItem("builderall-appshell--resources", JSON.stringify({
      history, whiteLabel, user, translations, hasNotifications,
      accessRules: Array.from(accessRules),
      userQuotas: Array.from(userQuotas),
      whiteLabelData: Array.from(whiteLabelData),
    }));
  }

  mountAll(resources: Resources) {
    try {
      this.shadowRoot.innerHTML = require("bundle-text:./templates/index.html");

      render(() => HeaderLeft({
        token: this.getAttribute("token"),
        whiteLabel: resources.whiteLabel,
      }), this.shadowRoot.getElementById("header-left"));

      render(() => HeaderRight({
        token: this.getAttribute("token"),
        email: resources.user.email,
        name: resources.user.name
      }), this.shadowRoot.getElementById("header-right"));

      this.mountHeaderDropdown(resources);
      this.mountHeaderTooltip(resources);
      this.mountSidebarElements(resources);
      this.mountAvatarElements(resources);
      this.mountModals(resources);
      this.mountHistory(resources);
      this.mountHeaderMessage(resources);
      this.defineAddEventListenerToHeaderRight();
      this.applyDisableProperty();
    } catch (e) {
      console.error("[appshell] mount error", e);
    } finally {
      if (document.readyState === "complete") {
        this.defaultActions();
      } else {
        window.addEventListener("load", () => {
          this.defaultActions();
        });
      }
    }
  }

  applyDisableProperty() {
    const disable = this.getAttribute("disable");
    if (!disable) return;

    disable.split(',').forEach(value => {
      let element = this.shadowRoot?.querySelector<HTMLElement>(value);
      if (!element) return;

      element.classList.add('wrapper-disabled');

      // wrapper the element on a div to apply `cursor: not-allowed` style
      const wrapper = document.createElement('div');
      wrapper.style.cursor = 'not-allowed';
      element.parentNode?.insertBefore(wrapper, element);
      wrapper.appendChild(element);
    });
  }

  updateChildComponents() {
    this.querySelectorAll("builderall-banner, builderall-help, builderall-footer, builderall-enom, builderall-digital-file-repository, builderall-global-script")
      .forEach(child => {
        child.setAttribute("tool", this.getAttribute("tool"));
        child.setAttribute("token", this.getAttribute("token"));
      });
  }

  async mountHeaderMessage({ accessRules, translations }: Resources) {
    // Remove all old messages
    const oldMessages = this.shadowRoot!.querySelectorAll('.header-message-item');
    oldMessages.forEach(el => el.remove());

    const alerts = await this.sendRequest(`wrapper/alerts`);

    if (accessRules.has(`general-stuff|pay-now`) || accessRules.has(`general-stuff|unpause-account`) || alerts) {
      const headerMessageElement = this.shadowRoot!.querySelector<HTMLElement>(".header-message");
      headerMessageElement!.style.display = 'inline-block';
      const quotasUrl = process.env.OFFICE + '/us/office/edit-my-account?tab=quotas';

      const messagesGeneralStuff = [
        {
          slug: 'pay-now',
          p: translations["header-menu--pay-now--message"] || '',
          a: translations["header-menu--pay-now--title"] || 'Pay Now',
          href: this.proxyURL("general-stuff|pay-now"),
          type: 'ERROR'
        },
        {
          slug: 'unpause-account',
          p: translations["header-menu--unpause-account--message"] || '',
          a: translations["header-menu--unpause-account--title"] || 'Unpause',
          href: this.proxyURL("general-stuff|unpause-account"),
          type: 'ERROR'
        },
        ...alerts
      ];

      const closeButtonEl = headerMessageElement.querySelector('.btn-close-header');
      const currentDate = new Date();
      let carrousselTimer = null;
      // const userLang = getUserLang()

      for (let i = 0; i < messagesGeneralStuff.length; i++) {
        if (accessRules.has(`general-stuff|${messagesGeneralStuff[i].slug}`)) {
          const newEl1 = document.createElement('div');
          newEl1.className = `header-message-item ${messagesGeneralStuff[i].slug}`;
          let newP1 = document.createElement('p');
          let newLink1 = document.createElement('a');
          newP1.innerHTML = messagesGeneralStuff[i].p;
          newLink1.innerHTML = messagesGeneralStuff[i].a;
          newLink1.href = messagesGeneralStuff[i].href;
          newEl1.appendChild(newP1);
          newEl1.appendChild(newLink1);
          headerMessageElement.appendChild(newEl1);
          // Show close button
          closeButtonEl.style.display = 'none';
        } else if (messagesGeneralStuff[i].slug == 'billing-quotas-80-percent') {
          const whenShowAlert = new Date(messagesGeneralStuff[i].start_at);
          if (!whenShowAlert || currentDate >= whenShowAlert) {
            const newEl1 = document.createElement('div');
            newEl1.className = `header-message-item ${messagesGeneralStuff[i].type} ${messagesGeneralStuff[i].slug}`;
            let newP1 = document.createElement('p');
            let newLink1 = document.createElement('a');
            newP1.innerHTML = translations["appshell-quotas-80-alert"] || '';
            newLink1.innerHTML = translations["appshell-view-usage"] || '';
            newLink1.href = quotasUrl;
            newEl1.appendChild(newP1);
            newEl1.appendChild(newLink1);
            headerMessageElement.appendChild(newEl1);
            // Show close button
            closeButtonEl.style.display = 'flex';
          }
        } else if (messagesGeneralStuff[i].slug == 'billing-quotas-100-percent') {
          const whenShowAlert = new Date(messagesGeneralStuff[i].start_at);
          if (!whenShowAlert || currentDate >= whenShowAlert) {
            const newEl1 = document.createElement('div');
            newEl1.className = `header-message-item ERROR ${messagesGeneralStuff[i].slug}`;
            let newP1 = document.createElement('p');
            let newLink1 = document.createElement('a');
            newP1.innerHTML = translations["appshell-quotas-100-alert"] || '';
            newLink1.innerHTML = translations["appshell-click-here-preview-bill"] || '';
            newLink1.href = quotasUrl;
            newEl1.appendChild(newP1);
            newEl1.appendChild(newLink1);
            headerMessageElement.appendChild(newEl1);
            // Show close button
            closeButtonEl.style.display = 'flex';
          }
        } else if (messagesGeneralStuff[i].slug == 'scheduled-maintenance-notice-2025-04-18') {
          const newEl1 = document.createElement('div');
          newEl1.className = `header-message-item ${messagesGeneralStuff[i].type} ${messagesGeneralStuff[i].slug}`;
          let newP1 = document.createElement('p');
          newP1.innerHTML = translations["scheduled-maintenance-notice-2025-04-18"] || '';
          newEl1.appendChild(newP1);
          headerMessageElement.appendChild(newEl1);
        } else if (messagesGeneralStuff[i].is_admin) {
          const newEl1 = document.createElement('div');
          newEl1.className = `header-message-item admin-wrapper-alert ${messagesGeneralStuff[i].type} ${messagesGeneralStuff[i].slug}`;
          let newP1 = document.createElement('p');
          newP1.innerHTML = messagesGeneralStuff[i].message; // JSON.parse(messagesGeneralStuff[i].message)[userLang[0]] || JSON.parse(messagesGeneralStuff[i].message)[userLang[1]] || '';
          newEl1.appendChild(newP1);
          headerMessageElement.appendChild(newEl1);
        }
      }

      // Close button method
      closeButtonEl.addEventListener("click", async () => {
        const availableAlerts = ['billing-quotas-80-percent', 'billing-quotas-100-percent'];
        headerMessageElement.querySelectorAll('.header-message-item').forEach(async x => {
          const currentSlug = x.classList[2];
          if (x.style.display != 'none' && currentSlug && availableAlerts.includes(currentSlug)) {
            const data = new URLSearchParams();
            data.append('slug', currentSlug);
            data.append('show', 'false');

            await this.sendRequest(`profile/alert-visibility`, {
              method: "PUT",
              body: data
            });

            this.shadowRoot!.querySelector<HTMLElement>(`.${currentSlug}`).remove();
            this.shadowRoot!.querySelector<HTMLElement>('.ba-alert-profile').remove();
            clearInterval(carrousselTimer);
            const baContentEl = document.querySelector(".ba-app-content");
            baContentEl.classList.remove("has-pay-now", "has-header-message");
            this.mountHeaderMessage({ accessRules, translations });
          }
        })
      })

      // Mount carroussel
      const alertMessages = headerMessageElement.querySelectorAll('.header-message-item');
      const profileAlert = this.shadowRoot!.querySelector<HTMLElement>(".ba-alert-profile");
      const buttonsBox = headerMessageElement.querySelector('.header-buttons');
      buttonsBox.innerHTML = "";
      var currentAlert = 0;

      if (alertMessages.length > 0) {
        // Mount carroussel buttons
        let carrousselButtons = null;
        if (alertMessages.length > 1) {
          for (let i = 0; i < alertMessages.length; i++) {
            let newBtn = document.createElement('button');
            newBtn.className = `btn-carroussel-message ${currentAlert == i ? 'active-btn-message' : ''}`;
            buttonsBox.appendChild(newBtn);
          }
          carrousselButtons = buttonsBox.querySelectorAll('.btn-carroussel-message');
          carrousselTimer = setInterval(showAlerts, 5000);
        }

        showAlerts();

        function showAlerts(data) {
          if (!data) {
            currentAlert++;
            if (currentAlert == alertMessages.length || currentAlert > alertMessages.length) {
              currentAlert = 0;
            }
          }

          for (let i = 0; i < alertMessages.length; i++) {
            if (currentAlert == i) {
              if (alertMessages.length > 1) {
                carrousselButtons[i].className = 'btn-carroussel-message active-btn-message';
                alertMessages[i].style.display = 'flex';
              }
              // Profile alerts
              profileAlert.style.display = 'none';
              if (alertMessages[i].className.indexOf('billing-quotas-80-percent') != -1) {
                profileAlert.style.display = 'grid';
                profileAlert.className = 'ba-alert-profile ba-alert ba-warning';
                profileAlert.querySelector('span').innerHTML = translations["appshell-quotas-80-alert"] || '';
                let linkEl = profileAlert.querySelector('a');
                linkEl.innerHTML = translations["appshell-view-usage"] || '';
                linkEl.href = quotasUrl;
                // Show close button
                closeButtonEl.style.display = 'flex';
              } else if (alertMessages[i].className.indexOf('billing-quotas-100-percent') != -1) {
                profileAlert.style.display = 'grid';
                profileAlert.className = 'ba-alert-profile ba-alert ba-error';
                profileAlert.querySelector('span').innerHTML = translations["appshell-quotas-100-alert"] || '';
                let linkEl = profileAlert.querySelector('a');
                linkEl.innerHTML = translations["appshell-click-here-preview-bill"] || '';
                linkEl.href = quotasUrl;
                // Show close button
                closeButtonEl.style.display = 'flex';
              } else {
                // Hide close button
                closeButtonEl.style.display = 'none';
              }
            } else {
              carrousselButtons[i].className = 'btn-carroussel-message';
              alertMessages[i].style.display = 'none';
            }
          }
        }

        // Change carroussell active element
        if (!carrousselButtons == false) {
          for (let i = 0; i < carrousselButtons.length; i++) {
            carrousselButtons[i].addEventListener("click", () => {
              currentAlert = i;
              showAlerts(true);
              clearInterval(carrousselTimer);
              carrousselTimer = setInterval(showAlerts, 5000);
              carrousselButtons[i].className = 'btn-carroussel-message active-btn-message';
              for (let y = 0; y < carrousselButtons.length; y++) {
                if (currentAlert != y) {
                  carrousselButtons[y].className = 'btn-carroussel-message';
                }
              }
            });
          };
        }

        this.shadowRoot.querySelector<HTMLDivElement>("#dropdown-avatar")
          .style.maxHeight = "calc(100vh - 85px - 44px)"

        setTimeout(() => document.querySelector(".ba-app-content").classList.add("has-pay-now"), 1e3)
        setTimeout(() => document.querySelector(".ba-app-content").classList.add("has-header-message"), 1e3)
      }
    }
  }

  defaultActions(): void {
    const mainSidebarElement = this.shadowRoot!.querySelector<HTMLElement>(".main-sidebar");
    const wrapperHeaderElement = this.shadowRoot!.querySelector(".wrapper-header");
    const baAppContentElement = () => document.querySelector<HTMLElement>(".ba-app-content") || this.shadowRoot.getElementById("main-content");

    if (baAppContentElement()) {
      baAppContentElement()!.style.overflowY = "auto";
      baAppContentElement()!.style.transition = "filter 0.15s linear";
    }

    // z-index according access wrapper or maint content
    function moreZIndex5() {
      baAppContentElement()!.style.zIndex = "5";
    }

    function lessZIndex5() {
      baAppContentElement()!.style.zIndex = "-5";
    }

    wrapperHeaderElement?.addEventListener("mouseenter", () => {
      lessZIndex5();
    });

    wrapperHeaderElement?.addEventListener("mouseleave", () => {
      let timer = setTimeout(() => {
        moreZIndex5();
      }, 200);
      wrapperHeaderElement.addEventListener("mouseenter", () => {
        clearTimeout(timer);
      });
      mainSidebarElement?.addEventListener("mouseenter", () => {
        clearTimeout(timer);
      });
    });

    mainSidebarElement?.addEventListener("mouseenter", () => {
      lessZIndex5();
    });

    mainSidebarElement?.addEventListener("mouseleave", () => {
      let timer = setTimeout(() => {
        moreZIndex5();
      }, 200);
      mainSidebarElement.addEventListener("mouseenter", () => {
        clearTimeout(timer);
      });
      wrapperHeaderElement?.addEventListener("mouseenter", () => {
        clearTimeout(timer);
      });
    });
  }

  showOrHideOverlay(data: String) {
    const baElement = () => document.querySelector<HTMLElement>(".ba-app-content") || this.shadowRoot.getElementById("main-content");

    const wrapperHeaderElement = this.shadowRoot?.querySelector(".wrapper-header");

    if (data == "show") {
      baElement()!.style.filter = "brightness(0.5)";
      wrapperHeaderElement?.classList.remove("show-shadow");
      return;
    }

    baElement()!.style.filter = "none";
    wrapperHeaderElement?.classList.add("show-shadow");
  }

  mountModals(resources: Resources) {
    render(() => FacebookCommunityModal({
      communities: [
        "builderall-br-affiliate-community",
        "builderall-br-users-community",
        "builderall-es-community",
        "builderall-es-affiliate-community",
        "builderall-us-official",
        "builderall-us-affiliate-community",
        "builderall-us-community",
        "builderall-de-community",
        "builderall-de-affiliate-community",
        "builderall-fr-community",
        "builderall-it-viral-team",
      ]
        .filter(slug => resources.accessRules.has(`facebook|${slug}`))
        .map(slug => ({
          title: resources.translations[`top-menu--connect--community--facebook--${slug}--title`],
          description: resources.translations[`top-menu--connect--community--facebook--${slug}--description`],
          href: this.proxyURL(`facebook|${slug}`),
        })),
    }), this.shadowRoot.getElementById("facebook-community-modal"));

    const tools = this.getLegacyTools(resources);

    if (Object.keys(tools).length > 0) {
      render(() => LegacyToolsModal({ tools }), this.shadowRoot.getElementById("legacy-tools-modal"));
    } else {
      this.shadowRoot.querySelector<HTMLButtonElement>(".dropdown-main-content-legacy-tools").style.display = 'none';
    }

    render(() => SendMessageToSponsorModal({ tools }), this.shadowRoot.getElementById("send-message-to-spondor-modal"));

    this.defineAddEventListenerToModals();
  }

  defineAddEventListenerToModals() {
    const modalLegacyTools = this.shadowRoot!.getElementById("legacy-tools-modal");
    const modalFacebookCommunity = this.shadowRoot!.getElementById("facebook-community-modal");
    const modalSendMessageToSponsor = this.shadowRoot!.getElementById("send-message-to-spondor-modal");
    const sendMessageToSponsorbtn = this.shadowRoot!.querySelector<HTMLButtonElement>('#btn-close-modal-enom');
    const sendMessageToSponsorTextarea = this.shadowRoot!.querySelector<HTMLButtonElement>('.modal-content-textarea');
    const sendMessageToSpondorRequest = this.shadowRoot!.querySelector<HTMLButtonElement>('.send-message-to-sponsor-btn');

    function inView(data, e) {
      return !data!.contains(e.target) && data?.style.display == "flex";
    }

    function closeModal(data) {
      data!.style.display = "none";
    }

    document.addEventListener("mouseup", (e) => {
      // Close modals
      if (inView(modalLegacyTools, e)) {
        closeModal(modalLegacyTools);
      } else if (inView(modalFacebookCommunity, e)) {
        closeModal(modalFacebookCommunity);
      }

    });
    modalSendMessageToSponsor.addEventListener("mouseup", (e) => {
      if (e?.target?.id == 'send-message-to-spondor-modal') {
        closeModal(modalSendMessageToSponsor);
        reseParams()
      }
    });
    sendMessageToSponsorbtn.addEventListener("mouseup", (e) => {
      closeModal(modalSendMessageToSponsor);
      reseParams()
    });
    function reseParams() {
      sendMessageToSponsorTextarea.value = ""
      sendMessageToSpondorRequest.classList.remove("ba-disabled")
      sendMessageToSpondorRequest.disabled = false
    }
  }

  getLegacyTools({ accessRules, translations }: Resources) {
    return [
      "pixel-perfect",
      "canvas",
      "video-wrapper-1",
      "ecommerce-magento",
      "roulette-1",
      "mobile-first",
      "instagram-autoresponder-1",
      "facebook-inbox-answer",
      "videotag-1",
      "wordpress-builder-1",
      "app-builder",
      "responsive-blog",
      "presentation-studio",
      "floating-videos",
      "responsive-builder",
      "omb-reviews",
      "market-place",
      "design-resources",
      "funnel-club",
      "viral-referrals",
      "seo-report",
      "website-agency",
      "branding-designs",
      "clickmap",
      "helpdesk",
    ]
      .filter(slug => accessRules.has(`legacy|${slug}`))
      .map(slug => ({
        title: translations[`top-menu--tools--legacy--${slug}--title`],
        href: this.proxyURL(`legacy|${slug}`),
        target: "_blank",
      }));
  }

  mountHeaderDropdown(resources: Resources) {
    const dropdownTemplate = require("bundle-text:./templates/dropdown-header-center.html");
    const headerDropdownItems = this.getHeaderDropdownItems(resources);

    function getToolIcon(className) {
      const iconMap = {
        'social-proof-pop-up': socialProofIcon,
        'browser-notification': browserNotificationIcon,
        'livechat': livechatIcon,
        'exit-popup': exitPopupIcon,
        'meta-comment-autoresponder': autoresponderIcon,
      };

      return iconMap[className];
    }

    headerDropdownItems.forEach(it => {
      const li = document.createElement("li");

      if (it.type == "link") {
        let newLinkElement = document.createElement("a");
        newLinkElement.href = it.link;
        newLinkElement.target = it.target;
        newLinkElement.innerHTML = it.element;
        newLinkElement.className = "header-center-link";
        li.appendChild(newLinkElement);
      } else {
        li.innerHTML = dropdownTemplate;
        const dropdownToolsElement = li.querySelector<HTMLElement>(".dropdown.tools");
        const dropdownSectionsElement = li.querySelector<HTMLElement>(".dropdown.sections");
        // Set title to menu
        li.querySelector(".btn-title")!.textContent = it.element;

        // Set new value to hidden input with title and description about main element
        (<HTMLInputElement>li.querySelector(".header-center-hidden-input")).value = JSON.stringify([it.element, it.description]);

        /* Dropdown */
        if (it.type == "normal") {
          li.querySelector<HTMLElement>(".dropdown.normal")!.classList.add("show");
          // Set left content to dropdown
          this.resetDropdownData(li);

          const mainContent = li.querySelector(".dropdown-main-content");

          it.data.forEach(d => {
            let newMainDiv = document.createElement("div");
            newMainDiv.className = "dropdown-main-content-main-div";

            // Main content in dropdown with icon
            let newDiv = document.createElement("div");
            let newSpan = document.createElement("span");
            let newP = document.createElement("p");

            newDiv.className = "dropdown-main-content-title-column-div";
            newSpan.innerHTML = d.icon;
            newP.textContent = d.title;

            newDiv.appendChild(newSpan);
            newDiv.appendChild(newP);
            newMainDiv.appendChild(newDiv);

            // Elements in dropdown
            const newUl = document.createElement("ul");
            let hasShowFalse = false;
            d.data.forEach(e => {
              let newLi = document.createElement("li");
              let newA1 = document.createElement("a");
              let newA2 = document.createElement("a");
              let newHiddenInput = document.createElement("input");

              newA1.href = e.href;
              newA1.textContent = e.title;
              newA1.target = e.target;
              newA1.className = "link-tool";

              if (e.locked) {
                const lock = document.createElement("i");
                lock.className = "access-rules-lock";
                lock.innerHTML = require("bundle-text:./assets/lock.svg");
                lock.title = "Unavailable, requires plan upgrade";
                newA1.append(lock);
              }

              newA2.href = e.href;
              newA2.innerHTML = require("bundle-text:./assets/new-window.svg");
              newA2.target = "_blank";
              newA2.className = "link-blank-tool";

              newHiddenInput.type = "hidden";
              newHiddenInput.className = "input-dropdown-element-data";
              newHiddenInput.value = JSON.stringify([e.title, e.description]);

              if (!e.show) {
                newLi.className = "li-dont-show";
                hasShowFalse = true;
              }

              newLi.appendChild(newHiddenInput);
              newLi.appendChild(newA1);
              newLi.appendChild(newA2);
              newUl.appendChild(newLi);
              newMainDiv.appendChild(newUl);
            });

            // Show All Tools Button or Retract and Legacy button
            let newBottomDiv = document.createElement("div");
            newBottomDiv.className = "dropdown-main-content-bottom";

            if (hasShowFalse || Object.entries(d).length > 5) {
              let newButtonAllTools = document.createElement("button");
              newButtonAllTools.textContent = hasShowFalse ? "See more" : Object.entries(d).length > 5 ? "See less" : "";
              newButtonAllTools.className = "dropdown-main-content-btn all-tools";
              newBottomDiv.appendChild(newButtonAllTools);
            } else {
              let newSpan = document.createElement("span");
              newBottomDiv.appendChild(newSpan);
            }

            newMainDiv.appendChild(newBottomDiv);
            mainContent?.appendChild(newMainDiv);
          });
        } else if (it.type == "tools") {

          dropdownToolsElement?.classList.add("show");
          let mainContent = document.createElement("div");
          mainContent.className = "dropdown-main-content tools-menu";

          // Mount Superchage Tools Menu
          let superChargeDiv = document.createElement("div")
          let superChargeLogo = document.createElement("div")
          let superChargeTools = document.createElement("div")
          let superChargeHR = document.createElement("hr")

          superChargeDiv.className = "supercharge-main-content";
          superChargeTools.className = "supercharge-tools-list";
          superChargeLogo.className = "supercharge-logo";
          superChargeHR.className = "ba-hr-row"
          superChargeLogo.innerHTML =
            "<svg width=\"222\" height=\"63\" viewBox=\"0 0 222 63\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n" +
            "<path d=\"M7.917 33.581L5.945 35.089C5.945 35.089 5.79033 34.915 5.481 34.567C5.191 34.219 4.82367 33.697 4.379 33.001C3.95367 32.305 3.51867 31.464 3.074 30.478C2.62933 29.4727 2.25233 28.3223 1.943 27.027C1.653 25.7123 1.508 24.272 1.508 22.706C1.508 21.1207 1.653 19.6803 1.943 18.385C2.25233 17.0897 2.62933 15.949 3.074 14.963C3.51867 13.977 3.95367 13.1457 4.379 12.469C4.82367 11.773 5.191 11.251 5.481 10.903C5.79033 10.555 5.945 10.381 5.945 10.381L7.917 11.889C7.917 11.889 7.72367 12.121 7.337 12.585C6.96967 13.049 6.54433 13.745 6.061 14.673C5.57767 15.5817 5.15233 16.7127 4.785 18.066C4.41767 19.4 4.234 20.9467 4.234 22.706C4.234 24.1367 4.35 25.432 4.582 26.592C4.83333 27.7327 5.14267 28.738 5.51 29.608C5.87733 30.478 6.24467 31.203 6.612 31.783C6.97933 32.3823 7.28867 32.827 7.54 33.117C7.79133 33.4263 7.917 33.581 7.917 33.581ZM9.67466 24.881H11.7627C11.8207 25.693 12.0623 26.418 12.4877 27.056C12.913 27.694 13.493 28.1967 14.2277 28.564C14.9817 28.9313 15.8613 29.115 16.8667 29.115C17.7367 29.115 18.4617 28.9893 19.0417 28.738C19.641 28.4673 20.0857 28.1 20.3757 27.636C20.685 27.172 20.8397 26.6307 20.8397 26.012C20.8397 25.374 20.685 24.8617 20.3757 24.475C20.0663 24.0883 19.6217 23.7597 19.0417 23.489C18.4617 23.2183 17.7753 22.9573 16.9827 22.706C16.2287 22.474 15.465 22.2033 14.6917 21.894C13.9183 21.5847 13.1933 21.2077 12.5167 20.763C11.8593 20.3183 11.3277 19.7867 10.9217 19.168C10.5157 18.53 10.3127 17.7663 10.3127 16.877C10.3127 16.007 10.5447 15.195 11.0087 14.441C11.492 13.6677 12.188 13.049 13.0967 12.585C14.0247 12.1017 15.1363 11.86 16.4317 11.86C18.4617 11.86 19.9793 12.411 20.9847 13.513C21.99 14.615 22.4733 16.0747 22.4347 17.892H20.3467C20.308 16.5193 19.9213 15.5333 19.1867 14.934C18.452 14.3347 17.495 14.035 16.3157 14.035C15.3297 14.035 14.4693 14.267 13.7347 14.731C13 15.1757 12.6327 15.8813 12.6327 16.848C12.6327 17.2733 12.71 17.6503 12.8647 17.979C13.0387 18.2883 13.3093 18.5783 13.6767 18.849C14.0633 19.1197 14.5757 19.3903 15.2137 19.661C15.871 19.9123 16.6733 20.1927 17.6207 20.502C18.2393 20.6953 18.8677 20.9273 19.5057 21.198C20.163 21.4687 20.7623 21.807 21.3037 22.213C21.8643 22.619 22.309 23.141 22.6377 23.779C22.9857 24.3977 23.1597 25.1807 23.1597 26.128C23.1597 27.0367 22.9373 27.8873 22.4927 28.68C22.0673 29.4533 21.3907 30.0817 20.4627 30.565C19.554 31.0483 18.3747 31.29 16.9247 31.29C15.6873 31.29 14.624 31.1257 13.7347 30.797C12.8647 30.449 12.1493 30.0043 11.5887 29.463C11.028 28.9217 10.593 28.361 10.2837 27.781C9.99366 27.1817 9.80033 26.621 9.70366 26.099C9.607 25.577 9.59733 25.171 9.67466 24.881ZM37.7011 31H35.3811V16.5H37.7011V31ZM35.5261 24.243L35.5551 25.055C35.5358 25.287 35.4778 25.6447 35.3811 26.128C35.3038 26.6113 35.1588 27.1527 34.9461 27.752C34.7528 28.332 34.4628 28.8927 34.0761 29.434C33.7088 29.9753 33.2255 30.42 32.6261 30.768C32.0461 31.116 31.3211 31.29 30.4511 31.29C29.8905 31.29 29.3105 31.203 28.7111 31.029C28.1311 30.8743 27.5898 30.6133 27.0871 30.246C26.5845 29.8593 26.1785 29.3277 25.8691 28.651C25.5598 27.9743 25.4051 27.1333 25.4051 26.128V16.5H27.7251V25.548C27.7251 26.418 27.8605 27.1043 28.1311 27.607C28.4211 28.0903 28.8078 28.4383 29.2911 28.651C29.7745 28.8637 30.3158 28.97 30.9151 28.97C31.9011 28.97 32.7228 28.7187 33.3801 28.216C34.0375 27.7133 34.5401 27.0947 34.8881 26.36C35.2361 25.6253 35.4488 24.9197 35.5261 24.243ZM48.8697 31.319C47.7484 31.319 46.8107 31.0773 46.0567 30.594C45.3027 30.1107 44.7131 29.4823 44.2877 28.709C43.8624 27.9357 43.5531 27.114 43.3597 26.244C43.1664 25.374 43.0697 24.5523 43.0697 23.779C43.0697 22.735 43.1567 21.7587 43.3307 20.85C43.5241 19.9413 43.8334 19.139 44.2587 18.443C44.6841 17.747 45.2737 17.2057 46.0277 16.819C46.7817 16.4323 47.7291 16.239 48.8697 16.239C50.1264 16.239 51.2574 16.5677 52.2627 17.225C53.2681 17.863 54.0704 18.7523 54.6697 19.893C55.2884 21.0337 55.5977 22.329 55.5977 23.779C55.5977 25.2677 55.2884 26.5823 54.6697 27.723C54.0511 28.8443 53.2294 29.724 52.2047 30.362C51.1994 31 50.0877 31.319 48.8697 31.319ZM48.0867 28.883C49.0147 28.883 49.8364 28.6703 50.5517 28.245C51.2671 27.8003 51.8181 27.201 52.2047 26.447C52.6107 25.6737 52.8137 24.7843 52.8137 23.779C52.8137 22.2903 52.3981 21.0627 51.5667 20.096C50.7547 19.1293 49.5947 18.646 48.0867 18.646C47.1781 18.646 46.3661 18.8587 45.6507 19.284C44.9547 19.7093 44.4037 20.3087 43.9977 21.082C43.5917 21.8553 43.3887 22.7543 43.3887 23.779C43.3887 24.7457 43.5627 25.6157 43.9107 26.389C44.2781 27.143 44.8097 27.752 45.5057 28.216C46.2017 28.6607 47.0621 28.883 48.0867 28.883ZM40.6047 16.529H43.2727L43.3887 19.806V36.8H40.6047V16.529ZM68.8682 26.302H71.5072C71.3525 27.2493 70.9852 28.1 70.4052 28.854C69.8252 29.608 69.0615 30.2073 68.1142 30.652C67.1668 31.0967 66.0552 31.319 64.7792 31.319C63.2712 31.319 61.9275 31 60.7482 30.362C59.5688 29.724 58.6408 28.8443 57.9642 27.723C57.2875 26.6017 56.9492 25.2967 56.9492 23.808C56.9492 22.3 57.2778 20.9853 57.9352 19.864C58.5925 18.7233 59.5012 17.834 60.6612 17.196C61.8212 16.558 63.1455 16.239 64.6342 16.239C66.2002 16.239 67.5148 16.5483 68.5782 17.167C69.6415 17.7857 70.4245 18.704 70.9272 19.922C71.4492 21.1207 71.6618 22.619 71.5652 24.417H59.7042C59.8008 25.287 60.0618 26.0603 60.4872 26.737C60.9318 27.4137 61.5118 27.9453 62.2272 28.332C62.9618 28.7187 63.8028 28.912 64.7502 28.912C65.8135 28.912 66.7028 28.6703 67.4182 28.187C68.1335 27.7037 68.6168 27.0753 68.8682 26.302ZM64.6632 18.617C63.4065 18.617 62.3625 18.9553 61.5312 19.632C60.6998 20.2893 60.1488 21.169 59.8782 22.271H68.7812C68.7038 21.0917 68.2785 20.1927 67.5052 19.574C66.7318 18.936 65.7845 18.617 64.6632 18.617ZM73.6971 16.529H76.4811V31H73.6971V16.529ZM81.3821 18.994C80.3768 18.994 79.5165 19.226 78.8011 19.69C78.1051 20.154 77.5445 20.7243 77.1191 21.401C76.7131 22.0777 76.4425 22.7253 76.3071 23.344L76.2781 21.691C76.2975 21.4977 76.3458 21.198 76.4231 20.792C76.5198 20.3667 76.6841 19.893 76.9161 19.371C77.1481 18.849 77.4575 18.356 77.8441 17.892C78.2308 17.4087 78.7141 17.0123 79.2941 16.703C79.8741 16.3937 80.5701 16.239 81.3821 16.239V18.994ZM100.115 25.316C100.019 26.5727 99.6128 27.6553 98.8974 28.564C98.2014 29.4727 97.2348 30.1687 95.9974 30.652C94.7601 31.116 93.2908 31.348 91.5894 31.348C89.8108 31.348 88.2158 31.0677 86.8044 30.507C85.4124 29.927 84.3104 29.086 83.4984 27.984C82.7058 26.8627 82.3094 25.4803 82.3094 23.837C82.3094 22.1937 82.7058 20.8113 83.4984 19.69C84.3104 18.5687 85.4124 17.718 86.8044 17.138C88.2158 16.558 89.8108 16.268 91.5894 16.268C93.2908 16.268 94.7504 16.5097 95.9684 16.993C97.1864 17.457 98.1434 18.1337 98.8394 19.023C99.5548 19.9123 99.9704 20.9853 100.086 22.242H96.6354C96.3068 21.2753 95.6978 20.5213 94.8084 19.98C93.9191 19.4387 92.8461 19.168 91.5894 19.168C90.5841 19.168 89.6658 19.342 88.8344 19.69C88.0224 20.038 87.3651 20.56 86.8624 21.256C86.3791 21.9327 86.1374 22.793 86.1374 23.837C86.1374 24.8617 86.3791 25.722 86.8624 26.418C87.3458 27.0947 88.0031 27.607 88.8344 27.955C89.6658 28.2837 90.5841 28.448 91.5894 28.448C92.9428 28.448 94.0448 28.1773 94.8954 27.636C95.7654 27.0947 96.3551 26.3213 96.6644 25.316H100.115ZM115.252 31V23.083C115.252 22.2903 115.117 21.633 114.846 21.111C114.576 20.589 114.141 20.1927 113.541 19.922C112.961 19.6513 112.178 19.516 111.192 19.516C110.245 19.516 109.385 19.6997 108.611 20.067C107.838 20.415 107.21 20.879 106.726 21.459C106.243 22.0197 105.972 22.619 105.914 23.257L105.885 21.662C105.982 21.024 106.204 20.386 106.552 19.748C106.9 19.11 107.364 18.53 107.944 18.008C108.524 17.486 109.22 17.0703 110.032 16.761C110.864 16.4323 111.792 16.268 112.816 16.268C113.706 16.268 114.527 16.384 115.281 16.616C116.035 16.8287 116.693 17.167 117.253 17.631C117.814 18.095 118.249 18.675 118.558 19.371C118.868 20.0477 119.022 20.85 119.022 21.778V31H115.252ZM102.347 31V10.787H106.117V31H102.347ZM128.667 31.319C127.236 31.319 125.931 31.0097 124.752 30.391C123.592 29.753 122.664 28.8733 121.968 27.752C121.291 26.6113 120.953 25.2967 120.953 23.808C120.953 22.2807 121.301 20.9563 121.997 19.835C122.693 18.7137 123.63 17.8437 124.81 17.225C126.008 16.587 127.362 16.268 128.87 16.268C130.532 16.268 131.866 16.6063 132.872 17.283C133.896 17.9403 134.641 18.8393 135.105 19.98C135.569 21.1207 135.801 22.3967 135.801 23.808C135.801 24.6587 135.665 25.5287 135.395 26.418C135.124 27.288 134.708 28.1 134.148 28.854C133.587 29.5887 132.852 30.188 131.944 30.652C131.035 31.0967 129.943 31.319 128.667 31.319ZM129.885 28.419C131.025 28.419 132.011 28.2257 132.843 27.839C133.674 27.4523 134.312 26.911 134.757 26.215C135.201 25.519 135.424 24.7167 135.424 23.808C135.424 22.822 135.192 21.9907 134.728 21.314C134.283 20.618 133.645 20.096 132.814 19.748C132.002 19.3807 131.025 19.197 129.885 19.197C128.28 19.197 127.033 19.6223 126.144 20.473C125.254 21.3043 124.81 22.416 124.81 23.808C124.81 24.736 125.022 25.548 125.448 26.244C125.873 26.9207 126.463 27.4523 127.217 27.839C127.99 28.2257 128.879 28.419 129.885 28.419ZM135.424 16.587H139.194V31H135.685C135.685 31 135.656 30.8163 135.598 30.449C135.559 30.0623 135.52 29.579 135.482 28.999C135.443 28.419 135.424 27.8487 135.424 27.288V16.587ZM141.371 16.79H148.331V31H141.371V16.79ZM155.813 22.155C154.556 22.155 153.406 22.271 152.362 22.503C151.318 22.735 150.428 23.0153 149.694 23.344C148.959 23.6533 148.408 23.9627 148.041 24.272L147.983 22.88C148.041 22.474 148.195 22.0003 148.447 21.459C148.698 20.8983 149.036 20.328 149.462 19.748C149.887 19.1487 150.409 18.5977 151.028 18.095C151.646 17.573 152.352 17.1573 153.145 16.848C153.957 16.5193 154.846 16.355 155.813 16.355V22.155ZM168.627 28.999C165.882 28.999 163.62 28.7767 161.841 28.332C160.063 27.868 158.748 27.1817 157.897 26.273C157.047 25.345 156.621 24.185 156.621 22.793C156.621 21.4977 157.037 20.3763 157.868 19.429C158.7 18.4623 159.995 17.7083 161.754 17.167C163.533 16.6257 165.824 16.355 168.627 16.355C169.188 16.355 169.758 16.355 170.338 16.355C170.918 16.355 171.489 16.355 172.049 16.355H183.127V20.009C181.871 20.067 180.595 20.009 179.299 19.835C178.023 19.6417 176.844 19.3903 175.761 19.081L175.442 18.646C176.312 18.9167 177.134 19.2647 177.907 19.69C178.7 20.1153 179.338 20.6277 179.821 21.227C180.305 21.8263 180.546 22.5417 180.546 23.373C180.546 24.5717 180.131 25.5963 179.299 26.447C178.487 27.2783 177.202 27.9163 175.442 28.361C173.702 28.7863 171.431 28.999 168.627 28.999ZM174.804 37.235V36.452C174.804 35.872 174.543 35.524 174.021 35.408C173.519 35.3113 172.716 35.263 171.614 35.263H163.523C162.151 35.263 161.039 35.1663 160.188 34.973C159.357 34.7797 158.719 34.5187 158.274 34.19C157.849 33.8613 157.559 33.494 157.404 33.088C157.269 32.7013 157.201 32.3147 157.201 31.928C157.201 31.0967 157.511 30.449 158.129 29.985C158.748 29.5017 159.695 29.1633 160.971 28.97C162.247 28.7767 163.871 28.68 165.843 28.68L168.627 28.999C167.003 28.999 165.872 29.086 165.234 29.26C164.596 29.4147 164.277 29.7337 164.277 30.217C164.277 30.4877 164.403 30.6907 164.654 30.826C164.906 30.942 165.244 31 165.669 31H174.224C176.138 31 177.646 31.1353 178.748 31.406C179.85 31.696 180.624 32.1697 181.068 32.827C181.532 33.5037 181.764 34.422 181.764 35.582V37.235H174.804ZM168.627 25.2C169.903 25.2 170.928 25.1033 171.701 24.91C172.494 24.6973 173.064 24.417 173.412 24.069C173.76 23.721 173.934 23.3343 173.934 22.909C173.934 22.4257 173.76 22.01 173.412 21.662C173.064 21.314 172.504 21.0433 171.73 20.85C170.957 20.6567 169.923 20.56 168.627 20.56C167.351 20.56 166.317 20.6567 165.524 20.85C164.732 21.0433 164.152 21.314 163.784 21.662C163.417 22.01 163.233 22.4257 163.233 22.909C163.233 23.3343 163.407 23.721 163.755 24.069C164.123 24.417 164.703 24.6973 165.495 24.91C166.288 25.1033 167.332 25.2 168.627 25.2ZM202.931 25.896H209.775C209.601 27.0173 209.041 27.9937 208.093 28.825C207.165 29.6563 205.802 30.304 204.004 30.768C202.226 31.2127 199.954 31.435 197.189 31.435C194.27 31.435 191.747 31.203 189.62 30.739C187.513 30.275 185.889 29.492 184.748 28.39C183.608 27.288 183.037 25.7993 183.037 23.924C183.037 22.068 183.588 20.589 184.69 19.487C185.812 18.3657 187.426 17.5633 189.533 17.08C191.641 16.5967 194.193 16.355 197.189 16.355C200.128 16.355 202.535 16.616 204.41 17.138C206.286 17.66 207.668 18.5107 208.557 19.69C209.447 20.85 209.872 22.4257 209.833 24.417H190.113C190.21 24.9197 190.481 25.3837 190.925 25.809C191.389 26.2343 192.095 26.5823 193.042 26.853C194.009 27.1043 195.295 27.23 196.899 27.23C198.562 27.23 199.925 27.114 200.988 26.882C202.071 26.65 202.719 26.3213 202.931 25.896ZM196.899 20.56C194.85 20.56 193.284 20.7823 192.201 21.227C191.138 21.6717 190.51 22.1163 190.316 22.561H203.076C202.941 22.0583 202.4 21.604 201.452 21.198C200.505 20.7727 198.987 20.56 196.899 20.56ZM211.792 33.813C211.792 33.813 211.918 33.6777 212.169 33.407C212.44 33.1363 212.769 32.7207 213.155 32.16C213.542 31.5993 213.929 30.884 214.315 30.014C214.721 29.1247 215.05 28.0807 215.301 26.882C215.572 25.664 215.707 24.272 215.707 22.706C215.707 20.8887 215.504 19.3033 215.098 17.95C214.712 16.5967 214.257 15.4753 213.735 14.586C213.233 13.6773 212.778 13.0007 212.372 12.556C211.986 12.1113 211.792 11.889 211.792 11.889L213.996 9.685C213.996 9.685 214.199 9.86867 214.605 10.236C215.011 10.584 215.514 11.106 216.113 11.802C216.732 12.498 217.341 13.368 217.94 14.412C218.54 15.456 219.042 16.674 219.448 18.066C219.854 19.4387 220.057 20.9853 220.057 22.706C220.057 24.4267 219.854 25.9927 219.448 27.404C219.042 28.796 218.54 30.0237 217.94 31.087C217.341 32.1697 216.732 33.0687 216.113 33.784C215.514 34.5187 215.011 35.0697 214.605 35.437C214.199 35.8237 213.996 36.017 213.996 36.017L211.792 33.813Z\" fill=\"black\"/>\n" +
            "<path d=\"M74.638 54V40.392H81.568C83.234 40.392 84.508 40.707 85.39 41.337C86.286 41.953 86.734 42.849 86.734 44.025C86.734 44.781 86.559 45.383 86.209 45.831C85.873 46.265 85.39 46.594 84.76 46.818C84.13 47.028 83.388 47.161 82.534 47.217L82.618 47.091C83.234 47.105 83.815 47.161 84.361 47.259C84.921 47.343 85.411 47.497 85.831 47.721C86.265 47.945 86.601 48.26 86.839 48.666C87.091 49.058 87.217 49.569 87.217 50.199C87.217 51.039 87.007 51.732 86.587 52.278C86.167 52.824 85.586 53.244 84.844 53.538C84.102 53.818 83.248 53.972 82.282 54H74.638ZM77.032 51.963H81.925C82.835 51.963 83.542 51.809 84.046 51.501C84.55 51.179 84.802 50.682 84.802 50.01C84.802 49.478 84.648 49.093 84.34 48.855C84.032 48.603 83.633 48.442 83.143 48.372C82.667 48.302 82.156 48.267 81.61 48.267H77.032V51.963ZM77.032 46.272H81.421C82.331 46.272 83.031 46.118 83.521 45.81C84.025 45.488 84.277 44.984 84.277 44.298C84.277 43.626 84.025 43.15 83.521 42.87C83.031 42.576 82.387 42.429 81.589 42.429H77.032V46.272ZM99.4948 54H97.1638V43.542H99.4948V54ZM97.2898 49.023L97.3108 49.695C97.2828 49.877 97.2198 50.15 97.1218 50.514C97.0378 50.878 96.8908 51.277 96.6808 51.711C96.4848 52.131 96.2188 52.537 95.8828 52.929C95.5607 53.307 95.1547 53.622 94.6648 53.874C94.1748 54.112 93.5798 54.231 92.8797 54.231C92.3757 54.231 91.8647 54.161 91.3467 54.021C90.8427 53.895 90.3807 53.678 89.9607 53.37C89.5547 53.062 89.2257 52.642 88.9737 52.11C88.7217 51.578 88.5957 50.906 88.5957 50.094V43.542H90.9267V49.653C90.9267 50.311 91.0317 50.829 91.2417 51.207C91.4658 51.571 91.7738 51.83 92.1657 51.984C92.5578 52.138 93.0128 52.215 93.5308 52.215C94.2728 52.215 94.9028 52.047 95.4208 51.711C95.9388 51.375 96.3518 50.962 96.6598 50.472C96.9818 49.968 97.1918 49.485 97.2898 49.023ZM101.269 43.542H103.6V54H101.269V43.542ZM108.01 43.311C108.612 43.311 109.165 43.395 109.669 43.563C110.187 43.731 110.628 43.99 110.992 44.34C111.37 44.676 111.657 45.096 111.853 45.6C112.063 46.104 112.168 46.699 112.168 47.385V54H109.837V47.847C109.837 46.993 109.627 46.363 109.207 45.957C108.801 45.537 108.157 45.327 107.275 45.327C106.603 45.327 105.987 45.474 105.427 45.768C104.881 46.048 104.433 46.419 104.083 46.881C103.733 47.329 103.53 47.826 103.474 48.372L103.453 47.238C103.523 46.72 103.677 46.23 103.915 45.768C104.153 45.292 104.468 44.872 104.86 44.508C105.252 44.13 105.714 43.836 106.246 43.626C106.778 43.416 107.366 43.311 108.01 43.311ZM118.605 54.231C117.639 54.231 116.757 54 115.959 53.538C115.175 53.076 114.552 52.439 114.09 51.627C113.628 50.801 113.397 49.856 113.397 48.792C113.397 47.7 113.621 46.748 114.069 45.936C114.531 45.11 115.147 44.466 115.917 44.004C116.701 43.542 117.597 43.311 118.605 43.311C119.487 43.311 120.229 43.458 120.831 43.752C121.433 44.032 121.909 44.424 122.259 44.928C122.609 45.418 122.861 45.992 123.015 46.65C123.183 47.308 123.267 48.022 123.267 48.792C123.267 49.366 123.183 49.975 123.015 50.619C122.847 51.249 122.581 51.837 122.217 52.383C121.867 52.929 121.391 53.377 120.789 53.727C120.201 54.063 119.473 54.231 118.605 54.231ZM119.319 52.299C120.103 52.299 120.768 52.152 121.314 51.858C121.874 51.55 122.294 51.13 122.574 50.598C122.868 50.066 123.015 49.464 123.015 48.792C123.015 48.05 122.868 47.42 122.574 46.902C122.28 46.37 121.86 45.957 121.314 45.663C120.768 45.369 120.103 45.222 119.319 45.222C118.199 45.222 117.331 45.558 116.715 46.23C116.099 46.888 115.791 47.742 115.791 48.792C115.791 49.478 115.938 50.087 116.232 50.619C116.54 51.151 116.96 51.564 117.492 51.858C118.024 52.152 118.633 52.299 119.319 52.299ZM123.015 39.342H125.367V54H123.183C123.155 53.678 123.12 53.279 123.078 52.803C123.036 52.327 123.015 51.9 123.015 51.522V39.342ZM127.749 39.342H130.08V54H127.749V39.342ZM140.849 50.556H143.096C142.984 51.27 142.704 51.907 142.256 52.467C141.822 53.013 141.227 53.447 140.471 53.769C139.715 54.077 138.812 54.231 137.762 54.231C136.572 54.231 135.508 54.014 134.57 53.58C133.632 53.132 132.897 52.502 132.365 51.69C131.833 50.878 131.567 49.912 131.567 48.792C131.567 47.686 131.826 46.72 132.344 45.894C132.862 45.068 133.576 44.431 134.486 43.983C135.41 43.535 136.474 43.311 137.678 43.311C138.924 43.311 139.96 43.535 140.786 43.983C141.626 44.417 142.249 45.075 142.655 45.957C143.061 46.825 143.229 47.924 143.159 49.254H133.94C134.01 49.842 134.199 50.374 134.507 50.85C134.829 51.312 135.263 51.676 135.809 51.942C136.355 52.194 136.992 52.32 137.72 52.32C138.532 52.32 139.211 52.159 139.757 51.837C140.317 51.515 140.681 51.088 140.849 50.556ZM137.615 45.201C136.663 45.201 135.879 45.439 135.263 45.915C134.647 46.377 134.248 46.972 134.066 47.7H140.807C140.751 46.916 140.436 46.307 139.862 45.873C139.288 45.425 138.539 45.201 137.615 45.201Z\" fill=\"#01A2FD\"/>\n" +
            "</svg>\n";
          superChargeDiv.appendChild(superChargeLogo)

          it.data[0].data.forEach((element, index) => {
            let newA = document.createElement("a")
            let newToolSpan = document.createElement("span")
            let newSvg = document.createElement("img")

            newA.href = element.href
            newA.target = element.target
            newA.className = `supercharge-tool-div ${element.class}`
            newSvg.className = "supercharge-icon"
            newSvg.setAttribute('src', getToolIcon(element.class))

            newToolSpan.innerHTML = element.title

            newA?.appendChild(newToolSpan)
            newA?.appendChild(newSvg)
            superChargeTools?.appendChild(newA)
          })
          superChargeDiv?.appendChild(superChargeTools)
          mainContent?.appendChild(superChargeDiv)
          mainContent?.appendChild(superChargeHR)

          it.data.forEach((d, i) => {
            if (i == 0) {
              return true
            }
            let newMainDiv = document.createElement("div");
            newMainDiv.className = "dropdown-main-content-main-div";

            // Main content in dropdown with icon
            let newDiv = document.createElement("div");
            let newSpan = document.createElement("span");
            let newP = document.createElement("p");

            newDiv.className = "dropdown-main-content-title-column-div";
            newP.textContent = d.title;

            // show lock if don't have access
            let unlockLink = document.createElement("a");
            if (d.showUnlock) {
              newSpan.innerHTML = require("bundle-text:./assets/lock.svg");
              newDiv.appendChild(newSpan);
              newMainDiv.classList.add('locked');

              // unlock link
              unlockLink.href = this.proxyURL(d.slug);
              unlockLink.target = '_blank';
              unlockLink.className = 'ba-btn ba-md ba-blue unlock-link';
              unlockLink.textContent = resources.translations['appshell-unlock'] + ' ' + d.title;
            }

            newDiv.appendChild(newP);
            newMainDiv.appendChild(newDiv);

            // User Quotas
            let quotasDiv = document.createElement("div");
            let emailSpan = document.createElement("span");
            let subSpan = document.createElement("span");

            quotasDiv.className = "user-quotas";

            let emailSent = '0'
            let subscribers = '0'
            if (!this.isEmpty(d.userQuotas)) {
              emailSent = d.userQuotas['mailingboss-emails-sent']
              subscribers = d.userQuotas['mailingboss-subscribers']
            }
            emailSpan.textContent = emailSent + ' ' + resources.translations['sent-emails-mo'];
            subSpan.textContent = subscribers + ' ' + resources.translations['avatar-subscribers'];

            quotasDiv.appendChild(subSpan);
            quotasDiv.appendChild(emailSpan);
            newMainDiv.appendChild(quotasDiv);

            // Elements in dropdown
            const newUl = document.createElement("ul");

            d.data.forEach(e => {
              let newLi = document.createElement("li");
              let newA1 = document.createElement("a");
              let newA2 = document.createElement("a");
              let newHiddenInput = document.createElement("input");

              newA1.href = e.href;
              newA1.textContent = e.title;
              newA1.target = e.target;
              newA1.className = "link-tool";

              if (e.locked) {
                const lock = document.createElement("i");
                lock.className = "access-rules-lock";
                lock.innerHTML = require("bundle-text:./assets/lock.svg");
                lock.title = "Unavailable, requires plan upgrade";
                newA1.append(lock);
              }

              newA2.href = e.href;
              newA2.innerHTML = require("bundle-text:./assets/new-window.svg");
              newA2.target = "_blank";
              newA2.className = "link-blank-tool";

              newHiddenInput.type = "hidden";
              newHiddenInput.className = "input-dropdown-element-data";

              newHiddenInput.value = JSON.stringify([e.title, e.description, e.slug.split("|")[1]]);

              newLi.appendChild(newHiddenInput);
              newLi.appendChild(newA1);
              newLi.appendChild(newA2);
              newUl.appendChild(newLi);
              newMainDiv.appendChild(newUl);
            });

            newMainDiv?.appendChild(unlockLink);
            mainContent?.appendChild(newMainDiv);

            // Show All Tools Button or Retract and Legacy button
            let newBottomDiv = document.createElement("div");
            newBottomDiv.className = "dropdown-main-content-bottom";

            if (d.slug == "general-stuff|unlock-premium") {
              let newButtonLegacyTools = document.createElement("button");
              newButtonLegacyTools.className = "dropdown-main-content-legacy-tools";
              newButtonLegacyTools.textContent = "Legacy";
              newBottomDiv.appendChild(newButtonLegacyTools);
            }

            newMainDiv.appendChild(newBottomDiv);
          });

          /*
          // right content
          let rightContent = document.createElement("div");
          rightContent.className = 'dropdown-right-content';

          let newH2 = document.createElement("h2");
          newH2.className = 'dropdown-right-content-title';

          let newImg = document.createElement("img");
          newImg.className = 'dropdown-right-content-img';

          let newP = document.createElement("p");
          newP.className = 'dropdown-right-content-description';

          rightContent.appendChild(newH2);
          rightContent.appendChild(newImg);
          rightContent.appendChild(newP);

          let hrLine = document.createElement("hr")
          hrLine.className = 'dropdown-hr';
           */

          dropdownToolsElement?.appendChild(mainContent);
          /*
          dropdownToolsElement?.appendChild(hrLine);
          dropdownToolsElement?.appendChild(rightContent);
           */
        } else {
          dropdownSectionsElement?.classList.add("show");

          for (let i = 0; i < it.data.length; i++) {
            let newMainDiv = document.createElement("div");
            let newH2 = document.createElement("h2");
            let newSpan = document.createElement("span");
            let newHr = document.createElement("hr");

            newMainDiv.className = "dropdown-sections-content";
            newH2.textContent = it.data[i].title;
            newH2.className = "dropdown-sections-title";
            newSpan.innerHTML = (it.data[i] as any).description;
            newSpan.className = "dropdown-sections-description";
            newHr.className = "dropdown-hr";

            newMainDiv.appendChild(newH2);
            newMainDiv.appendChild(newSpan);
            let newDiv = document.createElement("div");
            newDiv.className = "dropdown-sections-content-elements";
            if (it.data[i].slug == "connect_community") {
              newDiv.classList.add("connect-community");
            } else if (it.data[i].slug == "connect_real_time_support") {
              newDiv.classList.add("connect-real-time-support");
            } else if (it.data[i].slug == "connect_help") {
              newDiv.classList.add("connect-help");
            }

            it.data[i].data.forEach(e => {
              let newLink = document.createElement("a");
              if (e.modal) {
                newLink.className = "open-facebook-community-modal";
              } else {
                newLink.href = e.href;
                newLink.target = e.target;
              }

              if (e.icon) {
                let newIconSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");

                newIconSvg.setAttribute("width", "40px");
                newIconSvg.setAttribute("height", "40px");

                newIconSvg.innerHTML = e.icon;
                newLink.appendChild(newIconSvg);
              }
              if (e.title) {
                let newTitleSpan = document.createElement("span");
                newTitleSpan.textContent = e.title;
                newLink.appendChild(newTitleSpan);
              }

              newDiv.append(newLink);
              newMainDiv.appendChild(newDiv);
            });
            if (i != 0) {
              dropdownSectionsElement?.appendChild(newHr);
            }
            dropdownSectionsElement?.appendChild(newMainDiv);
          }
        }
      }

      // Actions
      this.defineAddEventListenerToHeaderDropdown(li);
      this.shadowRoot?.getElementById("header-center-ul")!.appendChild(li);
    });
  }

  defineAddEventListenerToHeaderDropdown(element: HTMLElement) {
    const headerCenterElement = this.shadowRoot?.querySelector(".header-center");
    const headerCenterBtnElement = element.querySelector(".header-center-btn");
    const headerCenterLinkElement = element.querySelector(".header-center-link");
    const dropdownMainContentBtns = element.querySelectorAll(".dropdown-main-content-btn");
    const linkToolDropdownElement = element.querySelectorAll(".link-tool");
    const dropdownElement = element.querySelector<HTMLElement>(".dropdown.show");
    const dropdownMainContentLegacyToolsElement = element.querySelector(".dropdown-main-content-legacy-tools");
    const openFacebookCommunityModalElement = element.querySelector(".open-facebook-community-modal");
    const modalLegacyTools = this.shadowRoot!.getElementById("legacy-tools-modal");
    const modalFacebookCommunity = this.shadowRoot!.getElementById("facebook-community-modal");
    const mainSidebarElement = this.shadowRoot?.querySelector(".main-sidebar");
    const rm = this;

    // Show Dropdown
    function addActiveClass() {
      headerCenterBtnElement?.classList.add("active");
      rm.showOrHideOverlay("show");
    }

    function removeActiveClass(el) {
      if (el.classList.contains("active")) {
        el.classList.remove("active");
      }
    }

    function changeZIndex() {
      rm.shadowRoot?.querySelectorAll<HTMLElement>(".dropdown").forEach(el => el.style.zIndex = "-1");
    }

    headerCenterBtnElement?.addEventListener("mouseenter", () => {
      this.shadowRoot?.querySelectorAll<HTMLElement>(".dropdown").forEach(el => el.style.display = "none");
      this.resetDropdownData(element);
      if (dropdownElement) {
        dropdownElement.style.display = "flex";
      }
      // Add active class
      this.shadowRoot?.querySelectorAll<HTMLElement>(".header-center-btn").forEach(el => {
        // Remove active from all
        removeActiveClass(el);
      });
      addActiveClass();
      // Change z-index
      changeZIndex();
    });

    headerCenterLinkElement?.addEventListener("mouseenter", () => {
      this.shadowRoot?.querySelectorAll<HTMLElement>(".dropdown").forEach(el => el.style.display = "none");

      // Add active class
      this.shadowRoot?.querySelectorAll<HTMLElement>(".header-center-btn").forEach(el => {
        // Remove active from all
        removeActiveClass(el);
      });
      addActiveClass();
      // Change z-index
      changeZIndex();
    });

    // Hide Dropdown
    function closeDropdown() {
      if (dropdownElement) {
        dropdownElement.style.display = "none";
        // Doesn't remove overlay if mouse is inside sidbear
        if (!mainSidebarElement!.matches(":hover")) {
          rm.showOrHideOverlay("hide");
        }
      }
    }

    element.querySelector(".dropdown")?.addEventListener("mouseleave", () => {
      let timer = setTimeout(() => {
        closeDropdown();
        this.resetDropdownData(element);
      }, 200);
      headerCenterElement?.addEventListener("mouseenter", () => {
        clearTimeout(timer);
      });
    });

    headerCenterElement?.addEventListener("mouseleave", () => {
      let timer = setTimeout(() => {
        closeDropdown();
        this.resetDropdownData(element);
      }, 200);
      headerCenterElement?.addEventListener("mouseenter", () => {
        clearTimeout(timer);
      });
      // Change z-index
      this.shadowRoot?.querySelectorAll<HTMLElement>(".dropdown").forEach(el => el.style.zIndex = "3");
    });

    // Show All Tools or Hide Tools
    dropdownMainContentBtns.forEach((el: any) => {
      el.addEventListener("click", () => {
        if (el.classList.contains("all-tools")) {
          el.classList.remove("all-tools");
          el.textContent = "See less";
        } else {
          el.classList.add("all-tools");
          el.textContent = "See more";
        }

        // Set tools with display block or none
        let ul = el.parentElement.previousElementSibling;
        ul.querySelectorAll(".li-dont-show").forEach((el: HTMLElement) => {
          el.style.display = el.style.display == "flex" ? "none" : "flex";
        });
      });
    });

    // Show modal Legacy Tools
    dropdownMainContentLegacyToolsElement?.addEventListener("click", () => {
      modalLegacyTools!.style.display = "flex";
      modalLegacyTools!.style.backdropFilter = "brightness(0.5)";
    });

    // Show modal Facebook Community
    openFacebookCommunityModalElement?.addEventListener("click", () => {
      modalFacebookCommunity!.style.display = "flex";
      modalFacebookCommunity!.style.backdropFilter = "brightness(0.5)";
    });

    // Set new title and description in left content
    linkToolDropdownElement.forEach((el: any) => {
      el.addEventListener("mouseenter", () => {
        let data = JSON.parse(el.previousElementSibling.value);

        const titleEl = element.querySelector(".dropdown-left-content-title");
        titleEl!.textContent = data[0];

        const descEl = element.querySelector(".dropdown-left-content-description");
        descEl!.textContent = data[1];

        const titleRightEl = element.querySelector(".dropdown-right-content-title");
        titleRightEl!.textContent = data[0];

        const descRightEl = element.querySelector(".dropdown-right-content-description");
        descRightEl!.textContent = data[1];

        const imgRightEl = element.querySelector<HTMLImageElement>(".dropdown-right-content-img");
        imgRightEl!.src = !tools[data[2]]['img'] ? tools['website-builder']['img'] : tools[data[2]]['img'];
      });
    });
  }

  resetDropdownData(element: HTMLElement) {
    element.querySelector(".header-center-btn")?.classList.remove("active");

    const encoded = element.querySelector<HTMLInputElement>(".header-center-hidden-input")?.value;
    const [title, description] = encoded ? JSON.parse(encoded) : ["", ""];

    const titleElement = element.querySelector(".dropdown-left-content-title");
    if (titleElement) titleElement.textContent = title;

    const descriptionElement = element.querySelector(".dropdown-left-content-description");
    if (descriptionElement) descriptionElement.textContent = description;

    const titleRightElement = element.querySelector(".dropdown-right-content-title");
    if (titleRightElement) titleRightElement.textContent = title;

    const descriptionRightElement = element.querySelector(".dropdown-right-content-description");
    if (descriptionRightElement) descriptionRightElement.textContent = description;
  }

  getHeaderDropdownItems({ accessRules, translations, userQuotas }: Resources) {
    const mapTool = (prefix: string, t: any, i: number) => {
      const slug = `${prefix}|${t.slug}`;
      const hasAccess = accessRules.has(slug);
      return {
        ...t,
        slug,
        title: translations[`top-menu--tools--${t.slug}--title`],
        description: translations[`top-menu--tools--${t.slug}--description`],
        locked: !hasAccess,
        href: hasAccess
          ? this.proxyURL(slug)
          : this.proxyURL("general-stuff|checkout-plans"),
        target: hasAccess ? "_self" : "_blank",
        show: i < 5,
        class: t.slug
      };
    };

    return [
      {
        id: 0,
        element: translations["top-menu--tools--name"],
        slug: "tools",
        type: "tools",
        left_title: translations["top-menu--tools--title"],
        description: translations["top-menu--tools--description"],
        data: [
          {
            title: 'Superchage Plan',
            slug: 'general-stuff|unlock-supercharge',
            showUnlock: false,
            data: [
              {
                slug: "social-proof-pop-up"
              },
              {
                slug: "browser-notification"
              },
              {
                slug: "livechat"
              },
              {
                slug: "exit-popup"
              },
              {
                slug: "meta-comment-autoresponder"
              },
            ].map((t, i) => mapTool("supercharge", t, i)),
          },
          {
            title: translations["top-menu--tools--core--title"],
            slug: "general-stuff|unlock-core",
            showUnlock: accessRules.has("general-stuff|unlock-core"),
            data: [
              {
                slug: "website-builder",
              },
              {
                slug: "dns-manager", //top-menu--tools--dns-manager
              },
              {
                slug: "professional-email",
              },
              {
                slug: "url-shortener", // top-menu--tools--url-shortene
              },
            ].map((t, i) => mapTool("core", t, i)),
          },
          {
            title: translations["top-menu--tools--essentials--title"],
            slug: "general-stuff|unlock-essentials",
            showUnlock: accessRules.has("general-stuff|unlock-essentials"),
            userQuotas: userQuotas['essentials-plan'],
            data: [
              {
                slug: "email-marketing-5",
              },
              {
                slug: "booking",
              },
              {
                slug: "website-chatbot",
              },
              {
                slug: "wordpress",
              },
              {
                slug: "social-autopost"
              }
            ].map((t, i) => mapTool("essentials", t, i)),
          },
          {
            title: translations["top-menu--tools--advanced--title"],
            slug: "general-stuff|unlock-advanced",
            showUnlock: accessRules.has("general-stuff|unlock-advanced"),
            userQuotas: userQuotas['advanced-plan'],
            data: [
              {
                slug: "funnel-builder",
              },
              {
                slug: "whatsapp-central",
              },
              {
                slug: "sms",
              },
              {
                slug: "messenger-chatbot",
              },
              {
                slug: "crm", // top-menu--tools--crm
              },
              {
                slug: "elearning",
              },
              {
                slug: "team-access", // top-menu--tools--team-access
              },
            ].map((t, i) => mapTool("advanced", t, i)),
          },
          {
            title: translations["top-menu--tools--premium--title"],
            slug: "general-stuff|unlock-premium",
            showUnlock: accessRules.has("general-stuff|unlock-premium"),
            userQuotas: userQuotas['premium-plus-plan'],
            data: [
              {
                slug: "video-hosting",
              },
              {
                slug: "webinar",
              },
              {
                slug: "quiz",
              },
              {
                slug: "magazine-builder",
              },
              {
                slug: "telegram",
              },
            ].map((t, i) => mapTool("premium", t, i)),
          },
        ],
      },
      {
        id: 2,
        element: translations["top-menu--university--name"],
        slug: "university",
        type: "normal",
        left_title: translations["top-menu--university--title"],
        description: translations["top-menu--university--description"],
        data: [
          {
            title: translations["top-menu--university--training--title"],
            slug: "university_training",
            icon: require("bundle-text:./assets/university-training.svg"),
            data: [
              "email-marketing-mailingboss",
              "local-business-website-agency-course",
              "facebook-ads-course",
              "mailingboss-minicourse-practice",
              "builderall-university-live",
              "tybl-bonuses",
              "bootcamp",
              "es-niche-training",
              "mailingboss-extreme",
              "es-builderall-university",
              "cheetah-quick-website-creation",
              "viral-team-italia",
              "curso-afiliados-hotmart",
              "email-marketing-mastery-course",
              "marketing-machine-resources"
            ]
              .filter(slug => accessRules.has(`training|${slug}`))
              .map((slug, i) => ({
                title: translations[`top-menu--university--training--${slug}--title`],
                description: translations[`top-menu--university--training--${slug}--description`],
                href: this.proxyURL(`training|${slug}`),
                target: "_self",
                show: i < 5,
              })),
          },
          {
            title: translations["top-menu--university--add-ons--title"],
            slug: "university_add_ons",
            icon: require("bundle-text:./assets/university-add-ons.svg"),
            data: [
              "inbox-path",
              "solo-ads",
              "tybl-solo-ads",
            ]
              .filter(slug => accessRules.has(`add-ons|${slug}`))
              .map((slug, i) => ({
                title: translations[`top-menu--university--add-ons--${slug}--title`],
                description: translations[`top-menu--university--add-ons--${slug}--description`],
                href: this.proxyURL(`add-ons|${slug}`),
                target: "_blank",
                show: i < 5,
              })),
          },
        ],
      },
      {
        id: 3,
        element: translations["top-menu--connect--name"],
        slug: "connect",
        type: "sections",
        data: [
          {
            title: translations["top-menu--connect--community--title"],
            slug: "connect_community",
            description: translations["top-menu--connect--community--description"],
            data: [
              {
                icon: require("bundle-text:./assets/connect-linkedin.svg"),
                slug: "linkedin",
              },
              {
                icon: require("bundle-text:./assets/connect-facebook.svg"),
                modal: "facebook-community-modal",
                slug: "facebook",
              },
              {
                icon: require("bundle-text:./assets/connect-instagram.svg"),
                slug: "instagram",
              },
              {
                icon: require("bundle-text:./assets/connect-tiktok.svg"),
                slug: "tiktok",
              },
              {
                icon: require("bundle-text:./assets/connect-youtube.svg"),
                slug: "youtube",
              },
            ]
              .map(t => ({ ...t, slug: `community|${t.slug}` }))
              .map((t) => ({
                ...t,
                href: this.proxyURL(t.slug),
                target: "_blank",
              })),
          },
          {
            title: translations["top-menu--connect--real-time-support--title"],
            slug: "connect_real_time_support",
            description: translations["top-menu--connect--real-time-support--description"],
            data: [
              {
                icon: require("bundle-text:./assets/connect-ticket.svg"),
                slug: "ticket",
              },
              {
                icon: require("bundle-text:./assets/connect-chat.svg"),
                slug: "chat",
              },
              {
                icon: require("bundle-text:./assets/success-coaches.svg"),
                slug: "success-coaches",
              },
            ]
              .filter(t => accessRules.has(`real-time-support|${t.slug}`))
              .map((t) => ({
                ...t,
                title: translations[`top-menu--connect--real-time-support--${t.slug}--title`],
                href: this.proxyURL(`real-time-support|${t.slug}`),
                target: "_self",
              })),
          },
          {
            title: translations["top-menu--connect--help--title"],
            slug: "connect_help",
            description: translations["top-menu--connect--help--description"],
            data: [
              "support",
              "upwork",
              // "certified-partners",
              "knowledgebase",
              "blog",
              "events-calendar",
              "builderall-learn",
            ]
              .filter(slug => accessRules.has(`help|${slug}`))
              .map(slug => ({
                title: translations[`top-menu--connect--help--${slug}--title`],
                href: this.proxyURL(`help|${slug}`),
                target: slug === "blog" ? "_blank" : "_self",
              })),
          },
        ],
      },
      ...[
        {
          id: 4,
          element: translations["top-menu--franchise-system--name"],
          slug: "top-menu|franchise-system",
        },
        {
          id: 5,
          element: translations["top-menu--become-affiliate--name"],
          slug: "top-menu|become-affiliate",
        },
        {
          id: 6,
          element: translations["top-menu--concession--name"],
          slug: "top-menu|concession",
        },
      ]
        .filter(t => accessRules.has(t.slug))
        .map(t => ({
          ...t,
          type: "link",
          link: this.proxyURL(t.slug),
          target: "_self",
        })) as any[],
    ];
  }

  mountHeaderTooltip(resources: Resources) {
    const elRightMenu = this.shadowRoot.getElementById("header-right-ul");

    elRightMenu.innerHTML = this.getHeaderTooltipItems(resources)
      .map(({ slug, href, target, icon, tooltip }) => {
        let tag = slug == 'notifications' ? 'button' : 'a';
        return `
                    <li>
                        <${tag} class="header-right-btn ${tag == 'button' ? 'notifications-icon' : ''}" href="${href}" target="${tag == 'a' ? target : ''}">${icon}</${tag}>
                        <div class="tooltip">
                            <p class="tooltip-message">${tooltip}</p>
                        </div>
                    </li>`;
      }).join("\n");

    const elDropdownAvatarItem = this.shadowRoot.getElementById("dropdown-avatar");

    elRightMenu.querySelectorAll("li").forEach(elItem => {
      elItem.querySelector(".header-right-btn").addEventListener("mouseenter", () => {
        elItem.querySelector<HTMLElement>(".tooltip")!.style.display = "flex";
        elDropdownAvatarItem.style.display = "none";
        this.showOrHideOverlay("hide");
      });

      elItem.querySelector(".header-right-btn").addEventListener("mouseleave", () => {
        elItem.querySelector<HTMLElement>(".tooltip").style.display = "none";
      });
    });
  }

  getHeaderTooltipItems({ translations, hasNotifications }: Resources) {
    let notificationPath = require("bundle-text:./assets/normal_notification.svg");
    if (hasNotifications) {
      notificationPath = require("bundle-text:./assets/notification.svg");
    }

    return [
      {
        icon: require("bundle-text:./assets/knowledgebase.svg"),
        slug: "knowledgebase",
      },
      {
        icon: require("bundle-text:./assets/quick-meeting.svg"),
        slug: "quick-meeting",
      },
      {
        icon: require("bundle-text:./assets/support.svg"),
        slug: "support",
      },
      {
        icon: notificationPath,
        slug: "notifications",
      },
    ].map(t => ({
      ...t,
      tooltip: translations[`general-stuff--${t.slug}-title`],
      href: this.proxyURL(`general-stuff|${t.slug}`),
      target: t.slug === "quick-meeting" ? "_blank" : "_self",
    }));
  }

  defineAddEventListenerToHeaderRight() {
    const notificationsIconElement = this.shadowRoot?.querySelector<HTMLLinkElement>(".notifications-icon");

    notificationsIconElement.addEventListener("click", async () => {
      const href = notificationsIconElement.getAttribute('href');
      await this.sendRequest(`wrapper/notifications/read-all`, {
        method: "PUT",
      });
      window.open(href, "_self");
    })
  }

  mountSidebarElements(resources: Resources) {
    const sidebarTemplate = require("bundle-text:./templates/elements-main-sidebar.html");
    const sidebarItems = this.getSidebarItems(resources);

    sidebarItems.forEach(it => {
      if (it.show == true || it == "line") {
        const li = document.createElement("li");
        li.innerHTML = sidebarTemplate;
        const sidebarElementLinkElement: HTMLAnchorElement = li.querySelector(".sidebar-element-link");

        const setTitleAndIcon = (): void => {
          li.querySelector(".sidebar-element-icon")!.innerHTML = it.icon;
          li.querySelector(".sidebar-element-title")!.textContent = it.title;
        }

        if (it == "line" || it.data == "line") {
          let newHr = document.createElement("hr");
          li.appendChild(newHr);
          sidebarElementLinkElement!.style.display = "none";
        } else if (it.href && it.type == "link") {
          sidebarElementLinkElement!.href = it.href;
          sidebarElementLinkElement!.target = it.target || "_self";
          setTitleAndIcon();
          if (this.getAttribute("tool") === it.slug) li.classList.add("active");
        } else if (it.type == "dropdown") {
          setTitleAndIcon();

          // Data to right dropdown
          if (it.data) {
            li.querySelector(".sidebar-element-title")!.innerHTML += require("bundle-text:./assets/arrow-right.svg");
            li.querySelector(".dropdown-sidebar")?.classList.add("show");
            li.querySelector(".dropdown-title")!.textContent = it.data.title;
            li.querySelector(".dropdown-description")!.textContent = it.data.description;

            let content = li.querySelector(".dropdown-content");

            it.data.data.forEach(e => {
              let newA = document.createElement("a");
              newA.className = "sidebar-dropdown-element";
              let newDiv = document.createElement("div");
              let newP = document.createElement("p");
              let newSpan = document.createElement("span");

              if (e.toolkit.show) {
                newA.onclick = () => {
                  var gfr = document.createElement('builderall-digital-file-repository');

                  gfr.setAttribute("token", this.getAttribute("token"));
                  gfr.setAttribute("hideapplybutton", "true");
                  gfr.setAttribute("show", "true");
                  document.body.appendChild(gfr);

                  if (e.toolkit.slug == 'my-files') {
                    gfr.setAttribute("step", "files");
                  } else if (e.toolkit.slug == 'my-brands') {
                    gfr.setAttribute("step", "brands");
                  }
                }
              } else {
                newA.href = e.href;
                newA.target = e.target;
              }

              newP.textContent = e.title;
              content?.classList.add("lines");
              newSpan.innerHTML = require("bundle-text:./assets/line-arrow-right.svg");

              content?.appendChild(newA);
              newA.appendChild(newDiv);
              newDiv.appendChild(newP);
              if (e.subtitle) {
                let newSubtitle = document.createElement("p");
                newSubtitle.textContent = e.subtitle;
                newDiv.className = "has-subtitle";
                newDiv.appendChild(newSubtitle);
              }
              newA.appendChild(newSpan);

              if (e.locked) {
                const lock = document.createElement("i");
                lock.className = "access-rules-lock";
                lock.innerHTML = require("bundle-text:./assets/lock.svg");
                lock.title = "Unavailable, requires plan upgrade";
                newP.append(lock);
              }
            });
          }
        }
        if (it.type === "modal") {
          setTitleAndIcon();
          li.addEventListener("click", () => this.openGetStartedModal());
        } else {
          // under construction
          setTitleAndIcon();
          // Show tooltip
          // sidebarElementLinkElement?.setAttribute("ba-tooltip-position", "right");
          // sidebarElementLinkElement?.setAttribute("ba-tooltip-title", it.tooltip);

          // li.setAttribute("title", it.tooltip);
        }

        // Actions
        this.defineAddEventListenerToSidebarElements(li);
        this.shadowRoot?.getElementById("main-sidebar-ul")!.appendChild(li);
      }
    });
  }

  defineAddEventListenerToSidebarElements(element: HTMLElement) {
    const mainSidebarElement = this.shadowRoot?.querySelector(".main-sidebar");
    const dropdownSidebarElement: HTMLDivElement = element.querySelector(".dropdown-sidebar");
    const sidebarElementLinkElement = element.querySelector(".sidebar-element-link");
    const headerCenterElement = this.shadowRoot!.querySelector(".header-center");
    const rm = this;

    // Open Sidebar
    mainSidebarElement?.addEventListener("mouseenter", () => {
      mainSidebarElement?.classList.add("opened");
      this.shadowRoot?.querySelector(".header-left")?.classList.add("show");
      rm.showOrHideOverlay("show");
    });

    // Close Sidebar and close dropdwons
    mainSidebarElement?.addEventListener("mouseleave", () => {
      let timer = setTimeout(() => {
        this.shadowRoot!.querySelector<HTMLElement>(".wrapper-main")!.style.height = "auto";
        this.shadowRoot?.querySelector(".header-left")?.classList.remove("show");
        mainSidebarElement?.classList.remove("opened");
        this.shadowRoot?.querySelectorAll<HTMLElement>(".sidebar-element-link").forEach(el => el.classList.remove("active"));
        element.querySelectorAll<HTMLElement>(".dropdown-sidebar").forEach(el => el.style.display = "none");

        // Doesn't remove overlay if mouse is inside header
        if (!headerCenterElement!.matches(":hover")) {
          rm.showOrHideOverlay("hide");
        }
      }, 200);
      dropdownSidebarElement?.addEventListener("mouseenter", () => {
        clearTimeout(timer);
      });
      mainSidebarElement?.addEventListener("mouseenter", () => {
        clearTimeout(timer);
      });
      // Set z-index
      this.shadowRoot?.querySelectorAll<HTMLElement>(".dropdown-sidebar").forEach(el => el.style.zIndex = "1");
    });

    // Hover in sidebar elements
    if (dropdownSidebarElement?.classList.contains("show")) {
      // Show Right Dropdown
      sidebarElementLinkElement?.addEventListener("click", e => {
        this.shadowRoot?.querySelectorAll<HTMLElement>(".dropdown-sidebar").forEach(el => {
          el.style.display = "none";
          el.style.zIndex = "-1";
          el.style.maxHeight = "auto";
          el.parentElement.querySelector(".sidebar-element-link").classList.remove("active");
        });

        element.querySelector<HTMLElement>(".dropdown-sidebar")!.style.display = "flex";
        sidebarElementLinkElement.classList.add("active");

        const linkTop = sidebarElementLinkElement.getBoundingClientRect().top;
        const dropdownHeight = dropdownSidebarElement.getBoundingClientRect().height;
        const dropdownMiddle = dropdownHeight / 2;

        const marginTop = 80 + 32;
        const offsetTop = linkTop - dropdownMiddle > marginTop ? dropdownMiddle : linkTop - marginTop;

        dropdownSidebarElement.style.top = `-${offsetTop}px`;

        const marginBottom = 32;
        const dropdownMaxHeight = window.innerHeight - (linkTop - offsetTop) - marginBottom;

        dropdownSidebarElement.style.maxHeight = `${dropdownMaxHeight}px`;
      });
    } else {
      // Remove active class and close all dropdowns
      sidebarElementLinkElement?.addEventListener("mouseenter", () => {
        this.shadowRoot?.querySelectorAll<HTMLElement>(".sidebar-element-link").forEach(el => el.classList.remove("active"));
        this.shadowRoot?.querySelectorAll<HTMLElement>(".dropdown-sidebar").forEach(el => el.style.display = "none");
      });
    }
  }

  getSidebarItems({ accessRules, translations }: Resources) {
    const mapTool = (prefix: string, slug: string) => {
      const hasAccess = accessRules.has(`${prefix}|${slug}`);
      return {
        title: translations[`left-menu--${prefix}--${slug}--title`],
        subtitle: translations[`left-menu--${prefix}--${slug}--subtitle`],
        locked: !hasAccess,
        href: hasAccess
          ? this.proxyURL(`${prefix}|${slug}`)
          : this.proxyURL("general-stuff|checkout-plans"),
        target: hasAccess ? "_self" : "_blank",
        toolkit: {
          show: ['my-brands', 'my-files'].includes(slug),
          slug: slug
        }
      };
    };

    return [
      {
        id: 1,
        icon: require("bundle-text:./assets/dashboard.svg"),
        title: translations["left-menu--home--name"],
        type: "link",
        show: true,
        href: this.proxyURL("left-menu|home"),
        slug: "office",
      },
      {
        data: "line",
        show: accessRules.has(`left-menu|get-started`)
      },
      {
        id: 2,
        icon: require("bundle-text:./assets/get-started.svg"),
        title: translations["left-menu--get-started--name"],
        type: "modal",
        show: accessRules.has(`left-menu|get-started`)
      },
      "line",
      {
        id: 3,
        icon: require("bundle-text:./assets/business-center.svg"),
        title: translations["left-menu--business-center--name"],
        type: "dropdown",
        show: true,
        data: {
          title: translations["left-menu--business-center--title"],
          description: translations["left-menu--business-center--description"],
          data: [
            "dns-manager",
            "my-brands",
            "my-files",
            "team-access",
          ].map(slug => mapTool("business-center", slug)),
        },
      },
      "line",
      {
        id: 4,
        icon: require("bundle-text:./assets/build.svg"),
        title: translations["left-menu--build--name"],
        type: "dropdown",
        show: true,
        data: {
          title: translations["left-menu--build--title"],
          description: translations["left-menu--build--description"],
          data: [
            "website-builder",
            "email-marketing-5",
            "booking",
            "elearning",
            "webinar",
            "magazine-builder",
          ].map(slug => mapTool("build", slug)),
        },
      },
      {
        id: 5,
        icon: require("bundle-text:./assets/engage.svg"),
        title: translations["left-menu--engage--name"],
        type: "dropdown",
        show: true,
        data: {
          title: translations["left-menu--engage--title"],
          description: translations["left-menu--engage--description"],
          data: [
            "email-marketing-5",
            "messenger-chatbot",
            "website-chatbot",
            "sms",
            "browser-notification",
            "social-proof-pop-up",
          ].map(slug => mapTool("engage", slug)),
        },
      },
      "line",
    ] as any;
  }

  async mountAvatarElements(resources: Resources) {
    const avatarElement = this.shadowRoot.getElementById('dropdown-avatar');
    const emailIcon1Element = this.shadowRoot!.querySelector<HTMLSpanElement>('.ai-email-icon-1');
    const emailIcon2Element = this.shadowRoot!.querySelector<HTMLSpanElement>('.ai-email-icon-2');
    const planNameIcon1Element = this.shadowRoot!.querySelector<HTMLSpanElement>('.da-pi-tl-plan-name-icon-1');
    const planNameIcon2Element = this.shadowRoot!.querySelector<HTMLSpanElement>('.da-pi-tl-plan-name-icon-2');


    var [profileUserData, profileQuotas, profileDomains, profileDomainsEmails] = await Promise.all([
      this.getProfileUserData(),
      this.getProfileQuotas(),
      this.getProfileDomains(),
      this.getProfileDomainsEmails()
    ]);

    if (profileUserData.user.privacy.hide_email) {
      emailIcon2Element.style.display = 'flex';
      emailIcon1Element.style.display = 'none';
    } else {
      emailIcon1Element.style.display = 'flex';
      emailIcon2Element.style.display = 'none';
    }

    avatarElement.querySelector('.da-ti-name').textContent = resources.translations['avatar-hi'] + ', ' + profileUserData.user.name;
    avatarElement.querySelector<HTMLLinkElement>('.da-ti-manage-account').href = this.proxyURL(`right-menu|settings`);

    // Define plan data
    var periodToPay = ''
    switch (profileUserData.plan?.period) {
      case 'ANUAL':
        periodToPay = resources.translations['avatar-yearly'];
        break;

      case 'SEMESTRAL':
        periodToPay = resources.translations['avatar-biannual'];
        break;

      case 'MENSAL':
        periodToPay = resources.translations['avatar-monthly'];
        break;

      case 'TRIMESTRAL':
        periodToPay = resources.translations['avatar-quarterly'];
        break;

      default:
        periodToPay = '';
    }

    if (profileUserData.plan?.title) {
      if (profileUserData.user.privacy.hide_plan) {
        planNameIcon2Element.style.display = 'flex';
        planNameIcon1Element.style.display = 'none';
      } else {
        planNameIcon1Element.style.display = 'flex';
        planNameIcon2Element.style.display = 'none';
      }
      avatarElement.querySelector('.da-pi-tl-plan-name').textContent = profileUserData.plan.title;
    }

    if (profileUserData.plan?.invoices_route) {
      avatarElement.querySelector<HTMLLinkElement>('.da-pi-top-plan-access').href = profileUserData.plan.invoices_route;
    } else {
      avatarElement.querySelector<HTMLLinkElement>('.da-pi-top-plan-access').style.display = 'none';
    }

    if (profileUserData.plan?.symbol) {
      avatarElement.querySelector('.da-pi-tl-plan-value').textContent = profileUserData.plan.symbol + profileUserData.plan?.value + '/' + periodToPay;
    }

    if (profileUserData.plan?.next_payment_date) {
      avatarElement.querySelector('.da-pi-tl-plan-next-payment').textContent = resources.translations['avatar-next-payment-date'] + ': ' + profileUserData.plan?.next_payment_date;
    }

    if (!resources.whiteLabelData?.is_builderall) {
      const planData = avatarElement.querySelectorAll('.da-pi-plan-data')

      planData.forEach(element => {
        (element as HTMLElement).style.display = 'none';
      })
    }

    if (!profileUserData.plan?.title && !profileUserData.plan?.invoices_route && !profileUserData.plan?.symbol && !profileUserData.plan?.next_payment_date) {
      avatarElement.querySelector<HTMLHRElement>('.da-plan-info .profile-line').style.display = 'none';
      avatarElement.querySelector<HTMLHRElement>('.da-plan-info .da-pi-top').style.display = 'none';
      avatarElement.querySelector<HTMLSpanElement>('.da-subtitle-plan').textContent = '';
    }

    const whiteLabelElement = avatarElement.querySelector<HTMLLinkElement>('.da-pi-tl-wl-access');
    if (profileUserData.plan?.is_white_label) {
      whiteLabelElement.href = this.proxyURL('right-menu|white-label');
    } else {
      whiteLabelElement.style.display = 'none';
    }

    // Define sponsor data
    if (profileUserData.user.parent) {
      avatarElement.querySelector<HTMLSpanElement>('.da-pi-bs-name')
        .textContent = profileUserData.user.parent.name;
    } else {
      avatarElement.querySelector<HTMLSpanElement>('.da-pi-bottom').style.display = 'none'
      avatarElement.querySelector<HTMLDivElement>('.da-pi-bottom-profile-line').style.display = 'none'
    }

    avatarElement.querySelector('.da-pi-bs-id').textContent = profileUserData.user.id;

    // Progress domains data
    const domainsProgressElement = avatarElement.querySelector<HTMLLinkElement>('.da-pi-me-domains');
    if (profileQuotas?.dns) {
      domainsProgressElement.href = this.proxyURL('dns-manager', {
        "w_redir": "1",
        "w_history_url": profileQuotas?.dns?.route,
        "w_type": "tool",
      });
    }

    const connectedDomains = profileQuotas?.dns?.usage?.connected || 0;
    const limitDomains = profileQuotas?.dns?.usage?.limit || 0;
    domainsProgressElement.querySelector<HTMLProgressElement>('#da-progress-domains').value = connectedDomains;
    domainsProgressElement.querySelector<HTMLProgressElement>('#da-progress-domains').setAttribute("max", Number(limitDomains).toString());
    domainsProgressElement.querySelector<HTMLProgressElement>('.da-pi-main-qty').textContent = connectedDomains + ' ' + resources.translations['avatar-of'] + ' ' + limitDomains;

    // Progress hard drive disk data
    const hardDriveSpaceProgressElement = avatarElement.querySelector<HTMLLinkElement>('.da-pi-me-hard-drive-space');
    if (profileQuotas?.storage) {
      hardDriveSpaceProgressElement.href = this.proxyURL('office', {
        "w_redir": "1",
        "w_history_url": profileQuotas?.storage?.route,
        "w_type": "tool",
      });
    }

    const storageInUse = profileQuotas.storage.in_use || 0;
    const storageReleased = profileQuotas.storage.released || 0;
    hardDriveSpaceProgressElement.querySelector<HTMLProgressElement>('#da-progress-hard-drive-space').value = storageInUse;
    hardDriveSpaceProgressElement.querySelector<HTMLProgressElement>('#da-progress-hard-drive-space').setAttribute("max", Number(storageReleased).toString());
    hardDriveSpaceProgressElement.querySelector<HTMLProgressElement>('.da-pi-main-qty-left').textContent = storageInUse + ' MB';
    hardDriveSpaceProgressElement.querySelector<HTMLProgressElement>('.da-pi-main-qty-right').textContent = this.convertMBtoGB(storageReleased) + ' GB';

    // Progress subscribers data
    const subscribersProgressElement = avatarElement.querySelector<HTMLLinkElement>('.da-pi-me-subscribers');
    if (profileQuotas?.mailingboss) {
      subscribersProgressElement.href = this.proxyURL('email-marketing-5', {
        "w_redir": "1",
        "w_history_url": profileQuotas?.mailingboss?.route,
        "w_type": "tool",
      });
    }

    const limitSubscribers = profileQuotas?.mailingboss?.limit_subscribers || 0;
    const mbSubscribers = profileQuotas?.mailingboss?.subscribers || 0;
    const mbPercentage = profileQuotas?.mailingboss?.percentage_usage || 0;

    subscribersProgressElement.querySelector<HTMLProgressElement>('#da-progress-subscribers').value = mbSubscribers;
    subscribersProgressElement.querySelector<HTMLProgressElement>('#da-progress-subscribers').setAttribute("max", Number(limitSubscribers).toString());

    subscribersProgressElement.querySelector<HTMLProgressElement>('.da-pi-main-qty-left').textContent = mbSubscribers + ' ' + resources.translations['avatar-of'] + ' ' + limitSubscribers;
    subscribersProgressElement.querySelector<HTMLProgressElement>('.da-pi-main-qty-right').textContent = mbPercentage;

    // Define domains list
    const listDomainsElement = avatarElement.querySelector<HTMLLinkElement>('.da-at-ts-list-domains');
    if (profileDomains.domains.length > 0) {
      profileDomains.domains.forEach(e => {
        let newLink = document.createElement("a");

        newLink.href = this.proxyURL("dns-manager", {
          "w_redir": "1",
          "w_history_url": (profileDomains?.route || "").replace(":domain-id", e.id),
          "w_type": "tool",
        });

        newLink.textContent = e.label;

        listDomainsElement.appendChild(newLink);
      });
    } else {
      let newLink = document.createElement("a");

      newLink.href = !profileDomains.route ? '' : profileDomains.route;
      newLink.textContent = resources.translations['avatar-no-domains'];

      listDomainsElement.appendChild(newLink);
    }

    // Define domain url
    avatarElement.querySelector<HTMLLinkElement>(".da-at-tool-domain")
      .href = this.proxyURL("dns-manager", {
        "w_redir": "0",
        "w_type": "tool",
      });

    const listDomainsEmailsElement = avatarElement.querySelector<HTMLLinkElement>('.da-at-ts-list-emails');
    if (profileDomainsEmails.domains_with_emails.length > 0) {
      profileDomainsEmails.domains_with_emails.forEach(e => {
        let newLink = document.createElement("a");

        newLink.href = profileDomainsEmails.route.replace(':domain-id', e.id);
        newLink.textContent = '@' + e.label;

        listDomainsEmailsElement.appendChild(newLink);
      });
    } else {
      let newLink = document.createElement("a");

      newLink.href = !profileDomains.route ? '' : profileDomains.route;
      newLink.textContent = resources.translations['avatar-no-emails'];

      listDomainsEmailsElement.appendChild(newLink);
    }

    // Define Language
    const languageSelectElement = avatarElement.querySelector('.da-language-select');
    const languages = [
      { value: 'us', slug: 'en_US', title: resources.translations['avatar-english'] },
      { value: 'br', slug: 'pt_BR', title: resources.translations['avatar-portuguese'] },
      { value: 'it', slug: 'it_IT', title: resources.translations['avatar-italian'] },
      { value: 'fr', slug: 'fr_FR', title: resources.translations['avatar-french'] },
      { value: 'es', slug: 'es_ES', title: resources.translations['avatar-spanish'] },
      { value: 'de', slug: 'de_DE', title: resources.translations['avatar-german'] },
    ];

    languages.forEach(e => {
      let newButton = document.createElement("button");
      newButton.textContent = e.title;
      newButton.value = e.value;

      languageSelectElement.appendChild(newButton);
    })

    avatarElement.querySelector('.da-lb-language-title').textContent = languages.find(x => x.slug == profileUserData.user.lang).title;

    // Logout button
    avatarElement.querySelector<HTMLLinkElement>('.da-logout-button').href = this.proxyURL(`right-menu|logout`);

    this.defineAddEventListenerToAvatarElements(avatarElement);
  }

  defineAddEventListenerToAvatarElements(element: HTMLElement) {
    const avatarSection = this.shadowRoot?.querySelector(".avatar-section");
    const dropdownElementLinkElement = element.querySelector(".dropdown-element-link");
    const dropdownAvatarElement = this.shadowRoot!.getElementById("dropdown-avatar");
    const copySponsorIdElement = this.shadowRoot!.getElementById("da-pi-bs-copy-sponsor");
    const languageSelectElement = this.shadowRoot!.querySelector('.da-language-select');
    const emailElement = this.shadowRoot!.querySelector<HTMLSpanElement>('.avatar-info-email');
    const emailIcon1Element = this.shadowRoot!.querySelector<HTMLSpanElement>('.ai-email-icon-1');
    const emailIcon2Element = this.shadowRoot!.querySelector<HTMLSpanElement>('.ai-email-icon-2');
    const planNameElement = this.shadowRoot!.querySelector<HTMLSpanElement>('.da-pi-tl-plan-name');
    const planNameIcon1Element = this.shadowRoot!.querySelector<HTMLSpanElement>('.da-pi-tl-plan-name-icon-1');
    const planNameIcon2Element = this.shadowRoot!.querySelector<HTMLSpanElement>('.da-pi-tl-plan-name-icon-2');
    const planValueElement = this.shadowRoot!.querySelector<HTMLSpanElement>('.da-pi-tl-plan-value');
    const openSendMessageToSponsorModalButton = this.shadowRoot!.querySelector<HTMLButtonElement>('.open-send-message-to-sponsor-modal');
    const sendMessageToSpondorModalElement = this.shadowRoot!.getElementById('send-message-to-spondor-modal');
    const modalSendMessageToSponsor = this.shadowRoot!.getElementById("send-message-to-spondor-modal");
    const closeSendMessageToSponsorModalButton = this.shadowRoot!.querySelector<HTMLButtonElement>('.open-send-message-to-sponsor-modal');
    const sendMessageToSpondorRequest = this.shadowRoot!.querySelector<HTMLButtonElement>('.send-message-to-sponsor-btn');
    const sendMessageToSponsorTextarea = this.shadowRoot!.querySelector<HTMLButtonElement>('.modal-content-textarea');
    const rm = this;
    const modalState: ToggleModal = {
      setToggleModal: true
    };

    // Open Main Dropdown
    avatarSection?.addEventListener("mouseenter", () => {
      dropdownAvatarElement!.style.display = "flex";
      rm.showOrHideOverlay("show");
    });

    // Hide Dropdown
    function closeDropdown() {
      dropdownAvatarElement!.style.display = "none";
      rm.showOrHideOverlay("hide");
    }

    dropdownAvatarElement?.addEventListener("mouseleave", () => {
      let timer = setTimeout(() => {
        closeDropdown();
      }, 200);
      dropdownElementLinkElement?.addEventListener("mouseenter", () => {
        clearTimeout(timer);
      });
      dropdownAvatarElement?.addEventListener("mouseenter", () => {
        clearTimeout(timer);
      });
    });

    // Copy sponsor id
    copySponsorIdElement.addEventListener("click", () => {
      const textToCopy = this.shadowRoot!.querySelector(".da-pi-bs-id").textContent;

      navigator.clipboard.writeText(textToCopy)
        .catch(function (err) {
          console.error('Error to copy sponsor id: ', err);
        });
    })

    // Show send message to sponsor modal
    openSendMessageToSponsorModalButton.addEventListener("click", () => {
      if (modalState.setToggleModal == true) {
        sendMessageToSpondorModalElement.style.display = "flex";
        sendMessageToSpondorModalElement.style.backdropFilter = "brightness(0.5)";
      }
    })

    closeSendMessageToSponsorModalButton.addEventListener("click", () => {
      if (!modalState.setToggleModal == true) {
        sendMessageToSpondorModalElement.style.display = "none";
      }
    })
    // Eye icon to show or hide elements
    // email
    const originalEmail = emailElement.textContent;

    if (emailIcon1Element.style.display == 'flex') {
      showEmail()
    } else {
      hideEmail()
    }


    emailIcon1Element?.addEventListener("click", async () => {
      hideEmail()

      const data = new URLSearchParams();
      data.append('type', 'email');
      data.append('value', 'true');

      await this.sendRequest(`profile/privacy-eye`, {
        method: "PUT",
        body: data
      });
    });

    sendMessageToSponsorTextarea.addEventListener("keydown", () => {
      if (sendMessageToSponsorTextarea.value.length > 1) {
        enableButton()
      } else {
        disableButton()
      }
    })

    function disableButton() {
      sendMessageToSpondorRequest.classList.add("ba-disabled")
      sendMessageToSpondorRequest.disabled = true
    }

    function enableButton() {
      sendMessageToSpondorRequest.classList.remove("ba-disabled")
      sendMessageToSpondorRequest.disabled = false
    }

    emailIcon2Element?.addEventListener("click", async () => {
      showEmail()

      const data = new URLSearchParams();
      data.append('type', 'email');
      data.append('value', 'false');

      await this.sendRequest(`profile/privacy-eye`, {
        method: "PUT",
        body: data
      });
    });

    sendMessageToSpondorRequest?.addEventListener("click", async () => {
      const data = new URLSearchParams();
      const { user } = await this.getProfileUserData();
      const test = sendMessageToSponsorTextarea.value;

      data.append('category', 'sponsoredMessage');
      data.append('message', user.email);
      data.append('description', test);
      data.append('from', 'office-api');
      data.append('owner', user.parent.email);
      try {
        disableButton()
        await this.sendRequest(`wrapper/notifications/create`, {
          method: "POST",
          body: data
        });
      } catch (error) {
        console.error(error, 'error');
      } finally {
        sendMessageToSpondorModalElement.style.display = "none";
        sendMessageToSponsorTextarea.value = "";
      }
    });

    if ("click" || sendMessageToSpondorRequest) {
      closeModalSendBtn();
    }

    function hideEmail() {
      emailIcon1Element.style.display = 'none';
      emailIcon2Element.style.display = 'block';
      emailElement.textContent = `${originalEmail.slice(0, 3)}••••••••••••••••`;
    }

    function showEmail() {
      emailIcon1Element.style.display = 'block';
      emailIcon2Element.style.display = 'none';
      emailElement.textContent = originalEmail;
    }
    function closeModalSendBtn() {
      sendMessageToSpondorModalElement.style.display = 'none';
    }

    // plan
    const planNameOrigial = planNameElement.textContent;
    const planValueOrigial = planValueElement.textContent;

    if (planNameIcon1Element.style.display == 'flex') {
      showPlanInformations()
    } else {
      hidePlanInformations()
    }

    planNameIcon1Element?.addEventListener("click", async () => {
      hidePlanInformations()

      const data = new URLSearchParams();
      data.append('type', 'plan');
      data.append('value', 'true');

      await this.sendRequest(`profile/privacy-eye`, {
        method: "PUT",
        body: data
      });
    });

    planNameIcon2Element?.addEventListener("click", async () => {
      showPlanInformations();

      const data = new URLSearchParams();
      data.append('type', 'plan');
      data.append('value', 'false');

      await this.sendRequest(`profile/privacy-eye`, {
        method: "PUT",
        body: data
      });
    });

    function hidePlanInformations() {
      planNameIcon1Element.style.display = 'none';
      planNameIcon2Element.style.display = 'block';
      planNameElement.textContent = '••••••';
      planValueElement.textContent = `${planValueOrigial.slice(0, 1)}••••••`;
    }

    function showPlanInformations() {
      planNameIcon1Element.style.display = 'block';
      planNameIcon2Element.style.display = 'none';
      planNameElement.textContent = planNameOrigial;
      planValueElement.textContent = planValueOrigial;
    }

    // Change language
    languageSelectElement.querySelectorAll("button").forEach(button => {
      button.addEventListener("click", async () => {
        this.shadowRoot!.querySelector<HTMLSpanElement>('.da-lb-language-title').textContent = button.textContent;

        await this.sendRequest("profile/update-lang", {
          method: "PUT",
          body: new URLSearchParams({ language: button.value }),
        });

        if (this.getAttribute("tool") == 'office') {
          location.reload();
        } else {
          window.location.replace(this.proxyURL(this.getAttribute("tool"), {
            "w_redir": "1",
            "w_type": "tool",
          }));
        }
      });
    });
  }

  isEmpty(val) {
    return val === undefined || val == null || val.length <= 0 || (typeof val === 'object' && Object.keys(val).length === 0) ? true : false;
  }

  convertMBtoGB(mb: number): number {
    return mb / 1024;
  }

  async getUserQuotas() {
    return await this.sendRequest("wrapper/plans-quotas")
  }

  async getProfileUserData() {
    return await this.sendRequest("profile");
  }

  async getProfileQuotas() {
    return await this.sendRequest("profile/quotas");
  }

  async getProfileDomains() {
    return await this.sendRequest("dns/domains");
  }

  async getProfileDomainsEmails() {
    return await this.sendRequest("dns/domains-with-emails");
  }

  isDivBelow(div1, div2) {
    const rect1 = div1.getBoundingClientRect();
    const rect2 = div2.getBoundingClientRect();

    return rect1.top >= rect2.bottom ||
      rect1.right <= rect2.left ||
      rect1.bottom <= rect2.top ||
      rect1.left >= rect2.right;
  }

  mountHistory(resources: Resources) {
    const { history, translations } = resources;

    const sidebar = this.shadowRoot.getElementById("main-sidebar-ul");

    sidebar.querySelectorAll(".sidebar-history-item")
      .forEach(el => el.remove());

    for (const slug of history) {
      const tool = tools[slug];
      if (!tool) {
        console.warn(`[appshell] tool with slug ${slug} not found`);
        continue;
      }

      const item = document.createElement("li");
      item.classList.add("sidebar-history-item");

      const anchor = document.createElement("a");
      anchor.classList.add("sidebar-element-link");
      anchor.href = this.proxyURL(slug, {
        "w_redir": "1",
        "w_type": "tool",
      });
      anchor.target = "_self";

      if (slug === this.getAttribute("tool")) {
        item.classList.add("is-active");
        anchor.href = "";
      }

      const icon = document.createElement("span");
      icon.classList.add("sidebar-element-icon");
      icon.innerHTML = tool.icon;

      const title = document.createElement("span");
      title.classList.add("sidebar-element-title");
      title.innerText = translations[`left-menu--history--${slug}--title`];

      const remove = document.createElement("div");
      remove.classList.add("sidebar-element-remove");
      remove.innerHTML = require("bundle-text:./assets/history-delete.svg");

      if (slug === this.getAttribute("tool")) {
        item.classList.add("is-active");
        anchor.href = "";
      }

      remove.addEventListener("click", async e => {
        e.preventDefault();
        e.stopPropagation();

        const { history } = await this.removeHistory(slug);
        resources.history = history;

        if (slug !== this.getAttribute("tool")) {
          this.mountHistory(resources);
          return;
        }

        const previousToolSlug = history.shift();
        if (!previousToolSlug) {
          window.open(this.proxyURL("left-menu|home"), "_self");
          return;
        }

        const previousTool = tools[previousToolSlug];
        if (!previousTool) {
          console.error(`tool with slug ${previousToolSlug} not found`);
          return;
        }

        window.open(this.proxyURL(previousTool.slug, {
          "w_redir": "1",
          "w_type": "tool",
        }), "_self");
      });

      anchor.append(icon, title, remove);
      item.append(anchor);
      sidebar.append(item);
    }
  }

  async syncHistory() {
    let defaultSlugs = ["ba-support"];

    if (defaultSlugs.includes(this.getAttribute("tool"))) {
      const { history } = await this.getHistory();
      return history;
    }

    const { redir, history } = await this.accessHistoryTick(window.location.href);
    if (redir) setTimeout(() => window.open(redir, "_self"));

    return history;
  }

  async getHistory(): Promise<{ redir?: string, history: string[] }> {
    if (!this.getAttribute("token") || !this.getAttribute("tool")) {
      throw new Error("'tool' attribute must be set before mounting history");
    }

    return await this.sendRequest(`wrapper/history?tool_slug=${this.getAttribute("tool")}`);
  }

  async createHistory(url: string): Promise<{ redir?: string, history: string[] }> {
    const form = new FormData();
    form.set("tool_slug", this.getAttribute("tool"));
    form.set("url", url);

    return await this.sendRequest("wrapper/history/create", {
      method: "POST",
      body: form,
    });
  }

  async removeHistory(slug: string): Promise<{ redir?: string, history: string[] }> {
    return await this.sendRequest(`wrapper/history/delete/${slug}`, {
      method: "DELETE",
    });
  }

  async accessHistoryTick(currentURL: string, previousURL: string = "") {
    try {
      if (currentURL !== previousURL) {
        return await this.createHistory(currentURL);
      }
    } catch (err) {
      console.error("register access history tick to api error", err);
    } finally {
      setTimeout(() => this.accessHistoryTick(window.location.href, currentURL), 500);
    }
  }

  async getAccessRules(): Promise<Set<string>> {
    const reduce = (arm: any) => typeof arm === "string"
      ? [arm] : Object.keys(arm).map(k => reduce(arm[k])).flat();

    return new Set<string>(reduce(await this.sendRequest("wrapper/access-rules")));
  }

  async getWhiteLabel(): Promise<{ logo: string, title: string } | false> {
    const { is_builderall, logo, title } = await this.sendRequest("wl-data");
    return is_builderall ? false : { logo, title };
  }

  async getWhiteLabelData(): Promise<any> {
    const response = await this.sendRequest("wl-data");

    console.log(response)
    return response;
  }

  async getUser(): Promise<{ id: number, name: string, email: string, lang: string }> {
    // return {
    // id: 103027,
    // name: "Anderson",
    // email: "anderson.marin@outlook.com",
    // lang: "pt_BR",
    // };
    return this.sendRequest("user");
  }

  async hasNotifications() {
    let response = await this.sendRequest("wrapper/notifications/all");
    return response.collection.some(x => x.read == false);
  }

  async sendRequest(path: string, options: RequestInit = {}) {
    return sendRequest(this.getAttribute("token"), path, options);
  }

  proxyURL(slug: string, queryParams: { [_: string]: string } = {}): string {
    return proxyURL(this.getAttribute("token"), slug, queryParams);
  }

  public openGetStartedModal({ onClose, onChange }: { onChange?, onClose?} = {}) {
    const modal = document.createElement("div");

    this.shadowRoot.append(modal);
    render(() => GetStartedModal({
      checkbox: !!(onClose || onChange),
      token: this.getAttribute("token"),
      onChange: (state) => {
        if (onChange) onChange(state);
      },
      onClose: (state) => {
        modal.remove();
        if (onClose) onClose(state);
      },
    }), modal);
  }
});